// Translation system for Docospot
// Contains all translations and language management functions

export const translations = {
    en: {
        // Common
        'common.save': 'Save',
        'common.cancel': 'Cancel',
        'common.delete': 'Delete',
        'common.deleteSuccess': 'Deleted successfully',
        'common.edit': 'Edit',
        'common.create': 'Create',
        'common.update': 'Update',
        'common.close': 'Close',
        'common.copy': 'Copy',
        'common.copied': 'Copied',
        'common.search': 'Search',
        'common.filter': 'Filter',
        'common.loading': 'Loading...',
        'common.noData': 'No data available',
        'common.required': 'Required',
        'common.optional': 'Optional',
        'common.name': 'Name',
        'common.address': 'Address',
        'common.yes': 'Yes',
        'common.no': 'No',
        'common.actions': 'Actions',
        'common.description': 'Description',
        'common.createdAt': 'Created',
        'common.updatedAt': 'Updated',
        'common.status': 'Status',
        'common.active': 'Active',
        'common.inactive': 'Inactive',
        'common.all': 'All',
        'common.none': 'None',
        'common.select': 'Select',
        'common.selected': 'Selected',
        'common.clear': 'Clear',
        'common.refresh': 'Refresh',
        'common.today': 'Today',
        'common.export': 'Export',
        'common.total': 'Total',
        'common.showing': 'Showing',
        'common.of': 'of',
        'common.note': 'Note',
        'common.account': 'account',
        'common.accounts': 'accounts',
        'common.location': 'location',
        'common.locations': 'locations',
        
        // Navigation
        'nav.dashboard': 'Dashboard',
        'nav.accounts': 'Accounts',
        'nav.users': 'Users',
        'nav.locations': 'Locations',
        'nav.sensors': 'Sensors',
        'nav.assets': 'Assets',
        'nav.groups': 'Groups',
        'nav.timeTracking': 'Time Tracking',
        'nav.alarms': 'Alarms',
        'nav.reports': 'Reports',
        'nav.reportTemplates': 'Report Templates',
        'nav.alarmTemplates': 'Alarm Templates',
        'nav.settings': 'Settings',
        'nav.logout': 'Logout',
        'nav.language': 'Language',
        'nav.setup': 'Setup',
        'nav.myAssets': 'My assets',
        'nav.tasks': 'Tasks (Beta)',
        'nav.signOut': 'Sign out',
        
        // Dashboard
        'dashboard.title': 'Dashboard',
        'dashboard.welcome': 'Welcome',
        'dashboard.welcomeBack': 'Welcome back',
        'dashboard.stats': 'Statistics',
        'dashboard.recentActivity': 'Recent Activity',
        'dashboard.accountInfo': 'Account Information',
        'dashboard.totalAccounts': 'Total Accounts',
        'dashboard.totalUsers': 'Total Users',
        'dashboard.totalLocations': 'Total Locations',
        'dashboard.accountHierarchy': 'Account Hierarchy',
        'dashboard.alarms': 'Alarms',
        'dashboard.loadingAccounts': 'Loading account structure...',
        'dashboard.loadingAlarms': 'Loading alarms...',
        'dashboard.at': 'at',
        
        // Accounts
        'accounts.title': 'Accounts',
        'accounts.add': 'Add New Account',
        'accounts.addButton': 'Add Account',
        'accounts.edit': 'Edit Account',
        'accounts.delete': 'Delete Account',
        'accounts.name': 'Account Name',
        'accounts.parent': 'Parent Account',
        'accounts.selectParent': 'Select Parent Account',
        'accounts.noParent': 'No Parent (Top Level)',
        'accounts.description': 'Description',
        'accounts.createSuccess': 'Account created successfully',
        'accounts.updateSuccess': 'Account updated successfully',
        'accounts.deleteSuccess': 'Account deleted successfully',
        'accounts.createError': 'Failed to create account',
        'accounts.updateError': 'Failed to update account',
        'accounts.deleteError': 'Failed to delete account',
        'accounts.deleteConfirm': 'Are you sure you want to delete the account',
        'accounts.deleteWarning': 'This will also affect all child accounts and associated data.',
        'accounts.hierarchy': 'Account Hierarchy',
        'accounts.refresh': 'Refresh',
        'accounts.loading': 'Loading accounts...',
        'accounts.directUsers': 'Direct Users',
        'accounts.totalUsers': 'Total Users',
        'accounts.plan': 'Plan',
        'accounts.search': 'Search Accounts',
        'accounts.searchPlaceholder': 'Search by account name...',
        'accounts.clear': 'Clear',
        'accounts.createTitle': 'Create New Account',
        'accounts.accountType': 'Account Type',
        'accounts.maxUsersOptional': 'Max Users (Optional)',
        'accounts.maxUsersLabel': 'Max Users',
        'accounts.maxUsersPlaceholder': 'Leave empty for unlimited',
        'accounts.unlimited': 'Unlimited',
        'accounts.statusSuspended': 'Suspended',
        'accounts.noAccountsAvailable': 'No accounts available. This will be a top-level account.',
        'accounts.noAccountsAvailableShort': 'No accounts available.',
        'accounts.errorLoadingAccounts': 'Error loading accounts',
        'accounts.accountNamePlaceholder': 'Enter account name',
        'accounts.parentAccountDesc': 'Select where this account sits in the hierarchy. Leave unselected for a top-level account.',
        'accounts.updateButton': 'Update Account',
        'accounts.createButton': 'Create Account',
        'accounts.enableTimeTracking': 'Enable Time Tracking',
        'accounts.enableSmartTime': 'Enable SmartTime Users',
        'accounts.enableTimeBudget': 'Enable Time Budget',
        'accounts.topLevelFeaturesHint': 'Only available for top-level accounts directly under Docospot. Turn features off to simplify the Location form.',
        
        // Users
        'users.title': 'Users',
        'users.add': 'Add New User',
        'users.addButton': 'Add User',
        'users.edit': 'Edit User',
        'users.delete': 'Delete User',
        'users.email': 'Email',
        'users.username': 'Username',
        'users.fullName': 'Full Name',
        'users.role': 'Role',
        'users.status': 'Status',
        'users.account': 'Account',
        'users.password': 'Password',
        'users.confirmPassword': 'Confirm Password',
        'users.createSuccess': 'User created successfully',
        'users.updateSuccess': 'User updated successfully',
        'users.deleteSuccess': 'User deleted successfully',
        'users.createError': 'Failed to create user',
        'users.updateError': 'Failed to update user',
        'users.deleteError': 'Failed to delete user',
        'users.deleteConfirm': 'Are you sure you want to delete the user',
        'users.deleteWarning': 'This action cannot be undone.',
        'users.roleUser': 'User',
        'users.roleAdmin': 'Administrator',
        'users.roleSuperAdmin': 'Super Administrator',
        'users.search': 'Search Users',
        'users.searchPlaceholder': 'Search by name, email, card ID...',
        'users.filterByAccount': 'Filter by Account',
        'users.filterByRole': 'Filter by Role',
        'users.anyAccount': 'Any account',
        'users.anyRole': 'Any role',
        'users.allRoles': 'All roles',
        'users.loadingAccounts': 'Loading accounts...',
        'users.clearAll': 'Clear all',
        'users.clear': 'Clear',
        'users.refresh': 'Refresh',
        'users.export': 'Export',
        'users.loading': 'Loading users...',
        'users.totalCount': 'Total users',
        'users.customizeColumns': '💡 Right-click table header to customize columns',
        'users.allLoaded': 'all loaded',
        'users.employeeId': 'Employee ID',
        'users.nfcCardId': 'NFC Card ID',
        'users.nfcCardIdHint': 'Separate multiple cards with commas',
        'users.createTitle': 'Create New User',
        'users.phoneNumber': 'Phone Number',
        'users.department': 'Department',
        'users.workAddress': 'Work Address',
        'users.sendWelcomeEmail': 'Send welcome email with setup link',
        'users.sendWelcomeEmailDesc': 'The user will receive an email with a secure link to set their password. The link expires in 7 days.',
        'users.resendWelcomeEmail': 'Send/Resend welcome email',
        'users.grantAccess': 'Grant Access',
        'users.noSystemAccess': 'Users without access cannot log in',
        'users.emailAlreadyRegistered': 'This email is already registered',
        'users.createUserButton': 'Create User',
        'users.updateUserButton': 'Update User',
        'users.noAccountsFound': 'No accounts found',
        'users.errorLoadingAccounts': 'Error loading accounts',
        'users.selectAccountRequired': 'Please select an account',
        'users.searchAccountsPlaceholder': 'Search accounts by name...',
        'users.users': 'users',
        
        // Locations
        'locations.title': 'Locations',
        'locations.add': 'Add New Location',
        'locations.addButton': 'Add Location',
        'locations.edit': 'Edit Location',
        'locations.delete': 'Delete Location',
        'locations.name': 'Name',
        'locations.address': 'Address',
        'locations.account': 'Account',
        'locations.selectAccount': 'Select Account',
        'locations.sensors': 'Associate Sensors',
        'locations.sensorsOptional': 'Associate Sensors (Optional)',
        'locations.sensorsHelp': 'Select sensors to associate with this location. Only unassociated sensors are shown.',
        'locations.timeBudget': 'Time Budget',
        'locations.timeBudgetOptional': 'Time Budget (Optional)',
        
        // Assets
        'assets.title': 'Assets',
        'assets.subtitle': 'Manage your tracked assets',
        'assets.create': 'Create Asset',
        'assets.createAsset': 'Create New Asset',
        'assets.editAsset': 'Edit Asset',
        'assets.searchPlaceholder': 'Search assets...',
        'assets.noAssets': 'No assets found',
        'assets.noAssetsDescription': 'Get started by creating your first asset',
        'assets.assetId': 'Asset ID',
        'assets.assetIdPlaceholder': 'Enter unique asset ID',
        'assets.assetIdHelp': 'Unique identifier for this asset (e.g., ASSET-001, VEH-123)',
        'assets.assetIdRequired': 'Asset ID is required',
        'assets.name': 'Asset Name',
        'assets.namePlaceholder': 'Enter asset name',
        'assets.homeLocation': 'Home Location',
        'assets.homeLocationOptional': 'Home Location (Optional)',
        'assets.noHomeLocation': 'No home location',
        'assets.homeLocationHelp': 'Optional: Default location for this asset',
        'assets.sensors': 'Associated Sensors',
        'assets.sensorsOptional': 'Associated Sensors (Optional)',
        'assets.sensorsHelp': 'Select sensors associated with this asset',
        'assets.selectAccountFirst': 'Select an account first to see available sensors',
        'assets.search': 'Search',
        'assets.searchAccounts': 'Search accounts...',
        'assets.filterByAccount': 'Filter by Account',
        'assets.anyAccount': 'Any Account',
        'assets.loadingAccounts': 'Loading accounts...',
        'assets.clearAll': 'Clear All',
        'assets.clear': 'Clear',
        'assets.refresh': 'Refresh',
        'assets.account': 'Account',
        'assets.accountCannotChange': 'Account cannot be changed when editing',
        'assets.nameRequired': 'Asset name is required',
        'assets.accountRequired': 'Account is required',
        'assets.createSuccess': 'Asset created successfully',
        'assets.updateSuccess': 'Asset updated successfully',
        'assets.deleteSuccess': 'Asset deleted successfully',
        'assets.deleteAsset': 'Delete Asset',
        'assets.deleteConfirm': 'Are you sure you want to delete "{name}"? This action cannot be undone.',
        'locations.timeBudgetPeriod': 'Period',
        'locations.timeBudgetHours': 'Hours',
        'locations.timeBudgetHoursOptional': 'Hours (Optional)',
        'locations.weekly': 'Weekly',
        'locations.monthly': 'Monthly',
        'locations.createSuccess': 'Location created successfully',
        'locations.updateSuccess': 'Location updated successfully',
        'locations.deleteSuccess': 'Location deleted successfully',
        'locations.createError': 'Failed to create location',
        'locations.updateError': 'Failed to update location',
        'locations.deleteError': 'Failed to delete location',
        'locations.deleteConfirm': 'Are you sure you want to delete the location',
        'locations.deleteWarning': 'This action cannot be undone.',
        'locations.requiredFields': 'Please fill in all required fields',
        'locations.search': 'Search Locations',
        'locations.searchPlaceholder': 'Search by name, address...',
        'locations.filterByAccount': 'Filter by Account',
        'locations.anyAccount': 'Any account',
        'locations.loadingAccounts': 'Loading accounts...',
        'locations.clearAll': 'Clear all',
        'locations.clear': 'Clear',
        'locations.refresh': 'Refresh',
        'locations.export': 'Export',
        'locations.loading': 'Loading locations...',
        'locations.allLoaded': 'all loaded',
        'locations.locations': 'locations',
        'locations.location': 'Location',
        'locations.sensors': 'Sensors',
        'locations.mapDescription': 'Move the map to see locations in different areas',
        'locations.nearbyLocations': 'Nearby Locations',
        'locations.distance': 'Distance',
        'locations.latestLog': 'Latest Log',
        'locations.noActivity': 'No activity',
        'locations.noLocationsFound': 'No locations found',
        'locations.checkIn': 'Check In',
        'locations.checkOut': 'Check Out',
        'locations.createTitle': 'Create New Location',
        'locations.locationName': 'Location Name',
        'locations.searchAccountsPlaceholder': 'Search accounts by name...',
        'locations.geocodeAddress': 'Geocode Address',
        'locations.coordinates': 'Coordinates',
        'locations.latitude': 'Latitude',
        'locations.longitude': 'Longitude',
        'locations.autoFilledGeocoding': 'Auto-filled when geocoding address',
        'locations.coordinatesHint': 'Optional: Coordinates for map display',
        'locations.sensorsLabel': 'Sensors',
        'locations.loadingSensors': 'Loading sensors...',
        'locations.selectSensorsHelp': 'Select sensors to associate with this location',
        'locations.externalContacts': 'External Contacts (Optional)',
        'locations.loadingUsers': 'Loading users...',
        'locations.selectExternalContactsHelp': 'Select external contact users who should be listed for this location',
        'locations.smarttimeUsers': 'SmartTime Users (Optional)',
        'locations.searchUsersToAdd': 'Search users to add...',
        'locations.noUsersAdded': 'No users added',
        'locations.smarttimeDesc': 'Add users and set expected daily hours',
        'locations.smarttimeConfigured': 'SmartTime configured',
        'locations.smarttimeUserCount': '{count} SmartTime user(s) configured',
        'locations.smarttimeNotConfigured': 'No SmartTime configured',
        'locations.budgetPeriod': 'Budget Period',
        'locations.daily': 'Daily',
        'locations.budgetHours': 'Budget Hours',
        'locations.budgetHoursDesc': 'Hours allocated per period',
        'locations.createButton': 'Create Location',
        'locations.updateButton': 'Update Location',
        'locations.noAccountsFound': 'No accounts found',
        'locations.selectAccountRequired': 'Please select an account',
        'locations.smarttimeHoursRequired': 'Please enter expected daily hours for all selected SmartTime users',
        'locations.massAlarm.assignTitle': 'Assign alarm to locations',
        'locations.massAlarm.assignDescription': 'Assign the selected alarm template to every sensor across {count} location(s).',
        'locations.massAlarm.assignConfirm': 'Assign',
        'locations.massAlarm.removeTitle': 'Remove alarms from locations',
        'locations.massAlarm.removeDescription': 'Remove alarm assignments from every sensor across {count} location(s).',
        'locations.massAlarm.removeConfirm': 'Remove',
        'locations.massAlarm.suspendTitle': 'Suspend alarms for locations',
        'locations.massAlarm.suspendDescription': 'Temporarily suspend alarm assignments across {count} location(s).',
        'locations.massAlarm.suspendDays': 'Suspend for (days)',
        'locations.massAlarm.suspendConfirm': 'Suspend',
        'locations.massAlarm.invalidDays': 'Enter days between 1 and 365',
        'locations.massAlarm.resumeTitle': 'Resume alarms for locations',
        'locations.massAlarm.resumeDescription': 'Clear suspension on alarm assignments across {count} location(s).',
        'locations.massAlarm.resumeConfirm': 'Resume',
        'locations.massAlarm.template': 'Alarm template',
        'locations.massAlarm.selectTemplate': 'Select an alarm template…',
        'locations.massAlarm.allTemplates': 'All alarm templates',
        'locations.massAlarm.templateRequired': 'Please select an alarm template',
        'locations.massAlarm.noSelection': 'No locations selected',
        'locations.massAlarm.loadTemplatesError': 'Failed to load alarm templates',
        'locations.massAlarm.created': 'Created',
        'locations.massAlarm.removed': 'Removed',
        'locations.massAlarm.updated': 'Updated',
        'locations.massAlarm.skipped': 'Skipped',
        'locations.massAlarm.incompatible': 'Incompatible sensor type',
        'locations.massAlarm.noSensors': 'The selected locations have no sensors.',
        'locations.massAlarm.noMatchingTemplates': 'No alarm templates match the sensor types in the selected locations ({types}).',
        'locations.massAlarm.locations': 'location(s)',
        'locations.massAlarm.sensors': 'sensor(s)',
        
        // Sensors
        'sensors.title': 'Sensors',
        'sensors.add': 'Add New Sensor',
        'sensors.addButton': 'Add Sensor',
        'sensors.importButton': 'Import Sensors',
        'sensors.importTitle': 'Import Sensors',
        'sensors.importDescription': 'Bulk import sensors from an Xtellio Excel package file.',
        'sensors.importFile': 'Excel file',
        'sensors.importFileHint': 'Select an .xlsx file containing a "serial_number" column.',
        'sensors.importSubmit': 'Import',
        'sensors.importing': 'Importing...',
        'sensors.importRequiredError': 'Please select a file, sensor type and account.',
        'sensors.importSuccess': 'Imported {count} sensor(s) successfully.',
        'sensors.importError': 'Failed to import sensors',
        'sensors.importResultImported': 'Imported',
        'sensors.importResultSkipped': 'Skipped (already exist)',
        'sensors.edit': 'Edit Sensor',
        'sensors.delete': 'Delete Sensor',
        'sensors.sensorId': 'Sensor ID',
        'sensors.name': 'Sensor Name',
        'sensors.type': 'Type',
        'sensors.battery': 'Battery Status',
        'sensors.location': 'Location',
        'sensors.asset': 'Asset',
        'sensors.account': 'Account',
        'sensors.status': 'Status',
        'sensors.lastSeen': 'Last Seen',
        'sensors.lastData': 'Last Data',
        'sensors.batteryLevel': 'Battery Level (mV)',
        'sensors.search': 'Search Sensors',
        'sensors.searchPlaceholder': 'Search by name, ID...',
        'sensors.filterByAccount': 'Filter by Account',
        'sensors.filterByBattery': 'Filter by Battery Status',
        'sensors.anyAccount': 'Any account',
        'sensors.allStatuses': 'All statuses',
        'sensors.loadingAccounts': 'Loading accounts...',
        'sensors.clearAll': 'Clear all',
        'sensors.clear': 'Clear',
        'sensors.refresh': 'Refresh',
        'sensors.export': 'Export',
        'sensors.loading': 'Loading sensors...',
        'sensors.totalCount': 'Total sensors',
        'sensors.customizeColumns': '💡 Right-click table header to customize columns',
        'sensors.selected': 'selected',
        'sensors.clearSelection': 'Clear',
        'sensors.bulkAssignAccount': 'Assign to account',
        'sensors.bulkAssignDescription': 'Move {count} selected sensor(s) to the account below. Any location or asset association will be cleared.',
        'sensors.bulkAssignConfirm': 'Assign',
        'sensors.selectAccountRequired': 'Please select an account.',
        'sensors.bulkUpdated': 'Updated',
        'sensors.bulkSkipped': 'Skipped',
        'sensors.createSuccess': 'Sensor created successfully',
        'sensors.updateSuccess': 'Sensor updated successfully',
        'sensors.deleteSuccess': 'Sensor deleted successfully',
        'sensors.createError': 'Failed to create sensor',
        'sensors.updateError': 'Failed to update sensor',
        'sensors.deleteError': 'Failed to delete sensor',
        'sensors.deleteConfirm': 'Are you sure you want to delete the sensor',
        'sensors.deleteWarning': 'This action cannot be undone.',
        'sensors.batteryGood': 'Good',
        'sensors.batteryLow': 'Low',
        'sensors.batteryCritical': 'Critical',
        'sensors.batteryRed': '🔴 Red (Critical)',
        'sensors.batteryYellow': '🟡 Yellow (Warning)',
        'sensors.batteryGreen': '🟢 Green (Good)',
        'sensors.batteryNoData': '⚪ No data',
        'sensors.unassociated': 'Unassociated',
        'sensors.accountSelected': 'account selected',
        'sensors.accountsSelected': 'accounts selected',
        'sensors.showing': 'Showing',
        'sensors.of': 'of',
        'sensors.sensorsLowercase': 'sensors',
        'sensors.scrollForMore': 'scroll for more',
        'sensors.total': 'Total',
        'sensors.showingFirst': 'showing first',
        'sensors.allLoaded': 'all loaded',
        'sensors.accountFilter': 'account filter',
        'sensors.sensorType': 'Sensor Type',
        'sensors.selectSensorType': 'Select sensor type',
        'sensors.typeTemperature': 'Temperature',
        'sensors.typeTempHumidity': 'Temperature/Humidity/Pressure',
        'sensors.typeTempHumidityBle': 'Temperature/Humidity',
        'sensors.typeTracker': 'Tracker',
        'sensors.typeGateway': 'Gateway',
        'sensors.typeCardReader': 'Card reader',
        'sensors.typeActivity': 'Activity',
        'sensors.typeLight': 'Light',
        'sensors.macAddress': 'BLE MAC address',
        'sensors.macAddressHint': 'Required to receive data when the gateway operates in Beacon mode. The MAC uniquely identifies this BLE sensor.',
        'sensors.statusMaintenance': 'Maintenance',
        'sensors.createButton': 'Create Sensor',
        'sensors.logsTitle': 'Sensor Logs',
        'sensors.recentLogs': 'Recent Logs',
        'sensors.noLogsFound': 'No logs found',
        'sensors.noLogsForDateRange': 'No logs found for selected date range',
        'sensors.rawJsonData': 'Raw JSON Data',
        'sensors.selectLogToView': 'Select a log to view details',
        'sensors.noAccountsFound': 'No accounts found',
        'sensors.errorLoadingAccounts': 'Error loading accounts',
        'sensors.errorLoadingLogs': 'Failed to load sensor logs',
        'sensors.requiredFieldsError': 'Please fill in all required fields (Name, ID, Type, and Account are mandatory)',
        
        // Assets
        'assets.title': 'Assets',
        'assets.add': 'Add New Asset',
        'assets.edit': 'Edit Asset',
        'assets.delete': 'Delete Asset',
        'assets.name': 'Asset Name',
        'assets.type': 'Type',
        'assets.account': 'Account',
        'assets.description': 'Description',
        'assets.serialNumber': 'Serial Number',
        'assets.location': 'Location',
        'assets.sensors': 'Associated Sensors',
        'assets.createSuccess': 'Asset created successfully',
        'assets.updateSuccess': 'Asset updated successfully',
        'assets.deleteSuccess': 'Asset deleted successfully',
        'assets.createError': 'Failed to create asset',
        'assets.updateError': 'Failed to update asset',
        'assets.deleteError': 'Failed to delete asset',
        'assets.deleteConfirm': 'Are you sure you want to delete the asset',
        'assets.deleteWarning': 'This action cannot be undone.',
        
        // Groups
        'groups.title': 'Groups',
        'groups.add': 'Add New Group',
        'groups.edit': 'Edit Group',
        'groups.delete': 'Delete Group',
        'groups.name': 'Group Name',
        'groups.description': 'Description',
        'groups.members': 'Members',
        'groups.account': 'Account',
        'groups.createSuccess': 'Group created successfully',
        'groups.updateSuccess': 'Group updated successfully',
        'groups.deleteSuccess': 'Group deleted successfully',
        'groups.createError': 'Failed to create group',
        'groups.updateError': 'Failed to update group',
        'groups.deleteError': 'Failed to delete group',
        'groups.deleteConfirm': 'Are you sure you want to delete the group',
        'groups.deleteWarning': 'This action cannot be undone.',
        
        // Time Tracking
        'time.title': 'Time Tracking',
        'time.add': 'Add Time Entry',
        'time.edit': 'Edit Time Entry',
        'time.delete': 'Delete Time Entry',
        'time.startTime': 'Start Time',
        'time.endTime': 'End Time',
        'time.duration': 'Duration',
        'time.location': 'Location',
        'time.user': 'User',
        'time.sensor': 'Sensor',
        'time.notes': 'Notes',
        'time.date': 'Date',
        'time.hours': 'Hours',
        'time.createSuccess': 'Time entry created successfully',
        'time.updateSuccess': 'Time entry updated successfully',
        'time.deleteSuccess': 'Time entry deleted successfully',
        'time.createError': 'Failed to create time entry',
        'time.updateError': 'Failed to update time entry',
        'time.deleteError': 'Failed to delete time entry',
        'time.deleteConfirm': 'Are you sure you want to delete this time entry',
        'time.deleteEvent': 'Delete Time Entry',
        'time.deleteWarning': 'This action cannot be undone.',
        'time.copyEvent': 'Copy Time Entry',
        'time.copyConfirm': 'Are you sure you want to create a copy of this time entry?',
        'time.copySuccess': 'Time entry copied successfully',
        'time.editEvent': 'Edit Time Entry',
        'time.eventUpdatedSuccess': 'Time entry updated successfully',
        'time.filterByLocation': 'Filter by Location',
        'time.filterByUser': 'Filter by User',
        'time.filterByDate': 'Filter by Date',
        'time.totalHours': 'Total Hours',
        'time.thisWeek': 'This Week',
        'time.thisMonth': 'This Month',
        'time.selectLocation': 'Select Location',
        'time.selectUser': 'Select User',
        'time.allLocations': 'All Locations',
        'time.allUsers': 'All Users',
        'time.dateRange': 'Date Range',
        'time.dateFrom': 'From Date',
        'time.dateTo': 'To Date',
        'time.to': 'to',
        'time.status': 'Status',
        'time.allStatuses': 'All',
        'time.completed': 'Completed',
        'time.open': 'Open',
        'time.modified': 'Modified',
        'time.manual': 'Manual',
        'time.currentValue': 'Current Value',
        'time.newTime': 'New Time',
        'time.reason': 'Reason',
        'time.reasonPlaceholder': 'e.g., Forgot to check out, Adjusted for break',
        'time.changeLog': 'Change Log',
        'time.totalChanges': 'Total Changes',
        'time.editedAt': 'Edited At',
        'time.editedBy': 'Edited By',
        'time.change': 'Change',
        'time.oldCheckIn': 'Old Check-In',
        'time.oldCheckOut': 'Old Check-Out',
        'time.newCheckIn': 'New Check-In',
        'time.newCheckOut': 'New Check-Out',
        'time.noEditHistory': 'No edit history available',
        'time.manualCreation': 'Manual Creation',
        'time.checkInEdit': 'Check-In Edit',
        'time.checkOutEdit': 'Check-Out Edit',
        'time.addEvent': 'Add Event',
        'time.searchUser': 'Search User',
        'time.searchUserPlaceholder': 'Search by name, email...',
        'time.account': 'Account',
        'time.allAccounts': 'All Accounts',
        'time.searchLocations': 'Search locations...',
        'time.loadingEvents': 'Loading time tracking events...',
        'time.userName': 'User Name',
        'time.employee': 'Employee',
        'time.events': 'events',
        'time.noEventsFound': 'No events found',
        'time.checkIn': 'Check In',
        'time.checkOut': 'Check Out',
        'time.checkInTime': 'Check-In Time',
        'time.checkOutTime': 'Check-Out Time',
        'time.totalTime': 'Total Time',
        'time.actions': 'Actions',
        'time.timeBudget': 'Time Budget',
        'time.budgetDaily': 'day',
        'time.budgetWeekly': 'week',
        'time.budgetMonthly': 'month',
        'time.timeTracking': 'Time Tracking',
        'time.viewMode': 'View Mode',
        'time.current': 'Current',
        'time.original': 'Original',
        'time.viewBy': 'View by',
        'time.weekly': 'Weekly',
        'time.monthly': 'Monthly',
        'time.daily': 'Daily',
        'time.selectPeriods': 'Select Periods',
        'time.onlyShowPeriodsWithActivity': 'Only show periods with activity',
        'time.budgetStatsDaysWithRegistration': 'Days with registration',
        'time.budgetStatsTotalTime': 'Total time',
        'time.budgetStatsAvgPerDay': 'Average per day',
        'time.addManualEvent': 'Add Manual Event',
        'time.checkInRequired': 'Check-In Time is required',
        'time.selectUserRequired': 'Please select a user',
        'time.selectLocationRequired': 'Please select a location',
        'time.eventCreatedSuccess': 'Time tracking event created successfully',
        'time.eventCreateError': 'Failed to create event',
        'time.userPlaceholder': 'Search and select a user...',
        'time.locationPlaceholder': 'Search and select a location...',
        'time.unknownCard': 'Unknown Card',
        'time.unknownCardMessage': 'This card is not assigned to any user. Copy the card ID below and assign it to the correct user in the Users section.',
        'time.cardId': 'Card ID',
        'time.cardIdCopied': 'Card ID copied to clipboard',
        'time.copyFailed': 'Failed to copy',
        'time.howToAssignCard': 'How to assign this card:',
        'time.copyCardId': 'Copy the card ID above',
        'time.goToUsers': 'Go to the Users section',
        'time.findUser': 'Find the correct user',
        'time.pasteCardId': 'Paste the card ID in the "NFC Card ID" field',
        'time.saveUser': 'Save the user, then click the unknown user again to resolve the name',
        'time.unknownUserFilter': 'Show only Unknown Users',
        'time.unknownLocation': 'Unknown Location',
        'time.unknownLocationMessage': 'This sensor is not assigned to any location. Copy the sensor ID below and assign it in the Setup section.',
        'time.sensorId': 'Sensor ID',
        'time.howToAssignSensor': 'How to assign this sensor:',
        'time.copySensorId': 'Copy the sensor ID above',
        'time.goToSetup': 'Go to the Setup section',
        'time.findSensor': 'Find this sensor (you can paste the ID in search)',
        'time.assignLocation': 'Assign the sensor to the correct location',
        'time.saveLocation': 'Save the configuration, then click the unknown location again to resolve',
        'time.noChanges': 'No changes made',
        'time.eventNotFound': 'Event not found',
        'time.enterAtLeastOneTime': 'Please enter at least one time',
        
        // Settings
        'settings.title': 'Settings',
        'settings.description': 'Configure application-wide settings and preferences',
        'settings.timeTracking': 'Time Tracking Settings',
        'settings.timeTrackingDescription': 'Control visibility of time tracking logs for external contacts',
        'settings.allowExternal': 'Allow external contacts to view time tracking logs',
        'settings.allowExternalDescription': 'When enabled, external contacts can see time tracking logs. Use the date restriction below to control which logs they can access.',
        'settings.restrictDate': 'Restrict logs up to a specific date',
        'settings.restrictDateDescription': 'Only show logs up to and including the selected date',
        'settings.showLogsUpTo': 'Show logs up to this date',
        'settings.noRestriction': 'No restriction - show all logs',
        'settings.delayDays': 'Delay access by number of days',
        'settings.delayDaysDescription': 'Only show logs older than the specified number of days',
        'settings.delayDaysLabel': 'Access delay (days)',
        'settings.accessRestrictionType': 'Access restriction',
        'settings.startDateRestriction': 'Hide logs before a specific date',
        'settings.startDateRestrictionDescription': 'Hide any logs older than the selected date from external contacts (e.g. when early data is unreliable).',
        'settings.startDateLabel': 'Show logs from this date',
        'settings.smartTimeTitle': 'SmartTime Settings',
        'settings.smartTimeMinDeviation': 'Minimum deviation from expected time (minutes)',
        'settings.smartTimeRandomDeviation': 'Random deviation range (minutes)',
        'settings.smartTimeDescription': 'SmartTime will adjust to expected time minus minimum deviation plus a random number within the range.',
        'settings.timeBudgetTitle': 'Time Budget Settings',
        'settings.timeBudgetRedLimitBelow': 'Red limit below budget (minutes)',
        'settings.timeBudgetRedLimitAbove': 'Red limit above budget (minutes)',
        'settings.timeBudgetRedLimitPlaceholder': 'Leave empty for no limit',
        'settings.timeBudgetDescription': 'Rows are green when close to budget and red when too far away. Set the number of minutes below and/or above budget at which a row turns red. Leave a field empty to never turn red on that side.',
        'settings.saveSettings': 'Save Settings',
        'settings.lastUpdated': 'Last updated',
        'settings.by': 'by',
        'settings.reportTemplates': 'Report Template Settings',
        'settings.reportTemplatesDescription': 'Configure default report templates for different features',
        'settings.timeTrackingReportTemplate': 'Default Time Tracking Report Template',
        'settings.timeTrackingReportTemplateDescription': 'Select a Filter report template to use for quick PDF generation from Time Tracking',
        'settings.environmentalReportTemplate': 'Default Sensor Report Template (Temp/Humidity)',
        'settings.environmentalReportTemplateDescription': 'Select a Filter report template to use for quick PDF generation from temperature/humidity sensor data',
        'insights.exportExcel': 'Export to Excel',
        'insights.exportPdf': 'PDF',
        'insights.pdfOptionsTitle': 'PDF Export Options',
        'insights.sortBy': 'Sort data by',
        'insights.dateNewestFirst': 'Date (newest first)',
        'insights.dateOldestFirst': 'Date (oldest first)',
        'insights.includeGraphs': 'Include graphs',
        'insights.includeTable': 'Include data point table',
        'insights.generatePdf': 'Generate PDF',
        'insights.generatingPdf': 'Generating PDF report...',
        'insights.sensorReport': 'Sensor Report',
        'settings.noneAskEveryTime': 'None - Ask every time',
        
        // Report Templates
        'reportTemplates.title': 'Report Templates',
        'reportTemplates.addTemplate': 'Add Template',
        'reportTemplates.copyTemplate': 'Copy Template',
        'reportTemplates.searchPlaceholder': 'Search templates...',
        'reportTemplates.loadingTemplates': 'Loading templates...',
        'reportTemplates.selectTemplate': 'Select a template',
        'reportTemplates.selectTemplateSubtitle': 'Choose a template from the left or create a new one',
        'reportTemplates.delete': 'Delete',
        'reportTemplates.save': 'Save',
        'reportTemplates.release': 'Release',
        'reportTemplates.preview': 'Preview',
        'reportTemplates.frontPage': 'Front Page',
        'reportTemplates.results': 'Results',
        'reportTemplates.documentation': 'Documentation',
        'reportTemplates.finalRemarks': 'Final Remarks',
        'reportTemplates.noTemplateSelected': 'No template selected',
        'reportTemplates.getStarted': 'Get started by creating a new report template',
        'reportTemplates.standardTexts': 'Standard Texts',
        'reportTemplates.tags': 'Tags',
        'reportTemplates.selectReportType': 'Select Report Type',
        'reportTemplates.chooseTemplateType': 'Choose the type of report template you want to create',
        'reportTemplates.dataCollection': 'Data Collection',
        'reportTemplates.dataCollectionDesc': 'Collect data with pictures, evaluations, and detailed documentation. Includes structured or dynamic layout options.',
        'reportTemplates.filterReport': 'Filter Report',
        'reportTemplates.filterReportDesc': 'Query and filter existing data to generate reports. Simple frontpage and results layout.',
        'reportTemplates.statisticsReport': 'Statistics Report',
        'reportTemplates.statisticsReportDesc': 'Generate statistical analysis and visualizations. Simple frontpage and results layout.',
        'reportTemplates.cancel': 'Cancel',
        'reportTemplates.statisticsComingSoon': 'Statistics reports are coming in a future update. Please choose Data Collection or Filter for now.',
        
        // Standard Texts
        'standardTexts.title': 'Standard Texts',
        'standardTexts.description': 'Organize reusable texts in categories',
        'standardTexts.addCategory': 'Add Category',
        'standardTexts.saveAll': 'Save All',
        'standardTexts.saved': 'Saved!',
        'standardTexts.noCategories': 'No categories yet',
        'standardTexts.clickToStart': 'Click "Add Category" to start organizing your standard texts',
        
        // Tags
        'tags.title': 'Report Tags',
        'tags.description': 'Create tag categories and tags for organizing reports and statistics',
        'tags.addCategory': 'Add Category',
        'tags.saveAll': 'Save All',
        'tags.saved': 'Saved!',
        
        // Asset Insights
        'assets.dataView': 'Data View',
        'assets.mapView': 'Map View',
        'assets.searchAssets': 'Search Assets',
        'assets.searchByName': 'Search by asset name...',
        'assets.loadingAssets': 'Loading assets...',
        'assets.selectAsset': 'Select an asset to view activity data',
        'assets.failedToLoad': 'Failed to load assets',
        'assets.noAssetsFound': 'No assets found',
        'assets.unnamedAsset': 'Unnamed Asset',
        'assets.24h': '24h',
        'assets.daily': 'Daily',
        'assets.threeDays': '3 Days',
        'assets.weekly': 'Weekly',
        'assets.monthly': 'Monthly',
        'assets.totalActivityAllTime': 'Total Activity (All Time)',
        'assets.activitySelectedPeriod': 'Activity for Selected Period',
        'assets.readings': 'readings',
        'assets.view': 'view',
        'assets.mapViewTitle': 'Map View',
        'assets.mapViewDescription': 'GPS tracking and location history will be displayed here.',
        'assets.comingSoon': 'Coming soon...',
        
        // Alarms
        'alarms.title': 'Alarms',
        'alarms.activeAlarms': 'Active Alarms',
        'alarms.history': 'History',
        'alarms.noActiveAlarms': 'No active alarms',
        'alarms.allAcknowledged': 'All alarms have been acknowledged.',
        'alarms.noHistory': 'No alarm history',
        'alarms.acknowledgedAppear': 'Acknowledged alarms will appear here.',
        'alarms.type': 'Type',
        'alarms.time': 'Time',
        'alarms.location': 'Location',
        'alarms.sensorName': 'Sensor Name',
        'alarms.info': 'Info',
        'alarms.action': 'Action',
        'alarms.acknowledged': 'Acknowledged',
        'alarms.notes': 'Notes',
        'alarms.acknowledge': 'Acknowledge',
        'alarms.addNote': 'Add note',
        'alarms.lastRegistration': 'Last registration',
        'alarms.acknowledgeSuccess': 'Alarm acknowledged successfully',
        'alarms.acknowledgeWithNoteSuccess': 'Alarm acknowledged with note successfully',
        'alarms.acknowledgeError': 'Failed to acknowledge alarm',
        'alarms.loadingError': 'Error loading alarms. Please try again.',
        'alarms.loadingActiveError': 'Error loading active alarms. Please try again.',
        'alarms.loadingHistoryError': 'Error loading alarm history. Please try again.',
        'alarms.addNoteTitle': 'Add Note',
        'alarms.addNotePrompt': 'Add an optional note for this acknowledgement:',
        'alarms.notesPlaceholder': 'Enter notes here...',
        'alarms.acknowledgeWithNote': 'Acknowledge with Note',
        'alarms.noRegistrationFound': 'No registration found for sensor',
        
        // Alarm Templates
        'alarmTemplates.title': 'Alarm Templates',
        'alarmTemplates.description': 'Create and manage reusable alarm templates that can be assigned to multiple sensors.',
        'alarmTemplates.templates': 'Templates',
        'alarmTemplates.filterByType': 'Filter by type:',
        'alarmTemplates.allTypes': 'All Types',
        'alarmTemplates.createTemplate': 'Create Template',
        'alarmTemplates.loadingTemplates': 'Loading templates...',
        'alarmTemplates.noTemplatesFilter': 'No templates found for the selected filter.',
        'alarmTemplates.tryDifferentFilter': 'Try selecting a different sensor type.',
        'alarmTemplates.noTemplatesYet': 'No alarm templates yet.',
        'alarmTemplates.createFirstTemplate': 'Create your first template to get started.',
        'alarmTemplates.schedule': 'Schedule',
        'alarmTemplates.check': 'Check:',
        'alarmTemplates.max': 'Max:',
        'alarmTemplates.days': 'Days:',
        'alarmTemplates.noSchedules': 'No schedules configured',
        'alarmTemplates.schedules': 'schedules',
        'alarmTemplates.sensor': 'sensor',
        'alarmTemplates.sensors': 'sensors',
        'alarmTemplates.smsRecipients': 'SMS Recipients:',
        'alarmTemplates.users': 'user(s)',
        'alarmTemplates.smsMessage': 'SMS Message:',
        'alarmTemplates.edit': 'Edit',
        'alarmTemplates.delete': 'Delete',
        'alarmTemplates.cardReader': 'Card Reader',
        'alarmTemplates.temperature': 'Temperature',
        'alarmTemplates.temperatureHumidity': 'Temperature/Humidity',
        'alarmTemplates.tracker': 'Tracker',
        'alarmTemplates.missingCheckin': 'Missing check-in report (account level)',
        'alarmTemplates.accounts': 'accounts',
        'alarmTemplates.reportTime': 'Report time:',
        'alarmTemplates.period': 'Period:',
        'alarmTemplates.hours': 'hours',
        'alarmTemplates.emailRecipients': 'Email recipients:',
        'alarmTemplates.manageAccounts': 'Manage accounts',
        'alarmTemplates.manageAccountsHelp': 'Select the accounts this alarm applies to. All locations under a selected account are monitored.',
        'alarmTemplates.testNow': 'Test now',
        'alarmTemplates.sendTestEmailPrompt': 'Do you want to send this report by email to the configured recipients now, as a test?',
        'alarmTemplates.sendTestEmail': 'Send test email',
        'alarmTemplates.testEmailSentTo': 'Test email sent to recipients:',
        'alarmTemplates.testEmailFailed': 'Failed to send the test email. Check that recipients have a valid email address.',
        
        // Alarm Template Modal
        'alarmTemplates.modal.create': 'Create Alarm Template',
        'alarmTemplates.modal.edit': 'Edit Alarm Template',
        'alarmTemplates.modal.loading': 'Loading...',
        'alarmTemplates.modal.sensorType': 'Sensor Type *',
        'alarmTemplates.modal.selectSensorType': 'Select sensor type...',
        'alarmTemplates.modal.sensorTypeHelp': 'This alarm template can only be assigned to sensors of this type',
        'alarmTemplates.modal.sensorTypeNoChange': 'Sensor type cannot be changed after creation',
        'alarmTemplates.modal.selectSensorTypePrompt': 'Please select a sensor type to configure alarm settings.',
        'alarmTemplates.modal.templateName': 'Template Name *',
        'alarmTemplates.modal.templateNamePlaceholder': 'e.g., Daily Check-In Alarm',
        'alarmTemplates.modal.description': 'Description',
        'alarmTemplates.modal.descriptionPlaceholder': 'Optional description of what this template is for',
        'alarmTemplates.modal.timezone': 'Timezone *',
        'alarmTemplates.modal.timezoneHelp': 'Detected from your browser. Alarm check times will run in this timezone.',
        'alarmTemplates.modal.timeSchedules': 'Time Schedules *',
        'alarmTemplates.modal.schedulesRange': '(1-7 schedules)',
        'alarmTemplates.modal.addSchedule': 'Add Schedule',
        'alarmTemplates.modal.scheduleNumber': 'Schedule',
        'alarmTemplates.modal.checkTime': 'Check Time *',
        'alarmTemplates.modal.maxTime': 'Max Time (minutes) *',
        'alarmTemplates.modal.activeDays': 'Active Days',
        'alarmTemplates.modal.sendSMS': 'Send SMS notifications',
        'alarmTemplates.modal.smsRecipientsLabel': 'SMS Recipients',
        'alarmTemplates.modal.noPhoneNumbers': 'No users have phone numbers configured. Please add phone numbers to user profiles to enable SMS notifications.',
        'alarmTemplates.modal.usersWithoutPhone': 'user(s) without phone numbers are not shown',
        'alarmTemplates.modal.holdCtrl': 'Hold Ctrl/Cmd to select multiple users',
        'alarmTemplates.modal.smsMessageLabel': 'SMS Message (optional)',
        'alarmTemplates.modal.smsMessageHelp': 'Message starts with location name (or sensor name if no location). Leave blank for default format.',
        'alarmTemplates.modal.smsMessagePlaceholder': 'e.g., No card registration detected',
        'alarmTemplates.modal.sendTestSMS': 'Send Test SMS',
        'alarmTemplates.modal.testSMSHelp': 'Sends a test message to selected recipients',
        'alarmTemplates.modal.comingSoon': 'Alarm Configuration Coming Soon',
        'alarmTemplates.modal.comingSoonDesc': 'Alarm templates for this sensor type are not yet available. Currently, only Card Reader sensors support alarm configuration.',
        'alarmTemplates.modal.cancel': 'Cancel',
        'alarmTemplates.modal.create': 'Create Template',
        'alarmTemplates.modal.update': 'Update Template',
        'alarmTemplates.modal.maxSchedules': 'Maximum 7 schedules allowed per template',
        'alarmTemplates.modal.missingCheckinIntro': 'This alarm runs once per day for a whole account and emails a list of the locations that had no check-in in the chosen period. Only locations with an NFC card reader are monitored. No email is sent when every location checked in.',
        'alarmTemplates.modal.reportTime': 'Report time *',
        'alarmTemplates.modal.reportTimeHelp': 'When the report is built, typically midnight',
        'alarmTemplates.modal.lookbackHours': 'Period (hours) *',
        'alarmTemplates.modal.lookbackHoursHelp': 'How far back to look for check-ins, typically 24',
        'alarmTemplates.modal.weekdays': 'Active days',
        'alarmTemplates.modal.emailRecipientsLabel': 'Email recipients *',
        'alarmTemplates.modal.emailRecipientsHelp': 'Team leaders and administrators with an email address. Hold Ctrl/Cmd to select multiple.',
        'alarmTemplates.modal.noEmailRecipients': 'No team leaders or administrators with an email address were found. Add an email address to their user profile first.',
        'alarmTemplates.modal.emailMessageLabel': 'Intro text (optional)',
        'alarmTemplates.modal.emailMessagePlaceholder': 'e.g., Please follow up on the locations below',
        'alarmTemplates.modal.templateNameRequired': 'Template name is required',
        'alarmTemplates.modal.reportTimeRequired': 'Report time is required',
        'alarmTemplates.modal.lookbackHoursInvalid': 'Period must be between 1 and 168 hours',
        'alarmTemplates.modal.emailRecipientsRequired': 'Select at least one email recipient',
        
        // Weekday abbreviations
        'weekdays.monday': 'Mon',
        'weekdays.tuesday': 'Tue',
        'weekdays.wednesday': 'Wed',
        'weekdays.thursday': 'Thu',
        'weekdays.friday': 'Fri',
        'weekdays.saturday': 'Sat',
        'weekdays.sunday': 'Sun',
        'weekdays.mon': 'Mo',
        'weekdays.tue': 'Tu',
        'weekdays.wed': 'We',
        'weekdays.thu': 'Th',
        'weekdays.fri': 'Fr',
        'weekdays.sat': 'Sa',
        'weekdays.sun': 'Su',
    },
    da: {
        // Common
        'common.save': 'Gem',
        'common.cancel': 'Annuller',
        'common.delete': 'Slet',
        'common.deleteSuccess': 'Slettet',
        'common.edit': 'Rediger',
        'common.create': 'Opret',
        'common.update': 'Opdater',
        'common.close': 'Luk',
        'common.copy': 'Kopier',
        'common.copied': 'Kopieret',
        'common.search': 'Søg',
        'common.filter': 'Filtrer',
        'common.loading': 'Indlæser...',
        'common.noData': 'Ingen data tilgængelig',
        'common.required': 'Påkrævet',
        'common.optional': 'Valgfri',
        'common.name': 'Navn',
        'common.address': 'Adresse',
        'common.yes': 'Ja',
        'common.no': 'Nej',
        'common.actions': 'Handlinger',
        'common.description': 'Beskrivelse',
        'common.createdAt': 'Oprettet',
        'common.updatedAt': 'Opdateret',
        'common.status': 'Status',
        'common.active': 'Aktiv',
        'common.inactive': 'Inaktiv',
        'common.all': 'Alle',
        'common.none': 'Ingen',
        'common.select': 'Vælg',
        'common.selected': 'Valgt',
        'common.clear': 'Ryd',
        'common.refresh': 'Opdater',
        'common.today': 'I dag',
        'common.export': 'Eksporter',
        'common.total': 'Total',
        'common.showing': 'Viser',
        'common.of': 'af',
        'common.note': 'Bemærk',
        'common.account': 'konto',
        'common.accounts': 'konti',
        'common.location': 'lokation',
        'common.locations': 'lokationer',
        
        // Navigation
        'nav.dashboard': 'Dashboard',
        'nav.accounts': 'Konti',
        'nav.users': 'Brugere',
        'nav.locations': 'Lokationer',
        'nav.sensors': 'Sensorer',
        'nav.assets': 'Aktiver',
        'nav.groups': 'Grupper',
        'nav.timeTracking': 'Tidsregistrering',
        'nav.alarms': 'Alarmer',
        'nav.reports': 'Rapporter',
        'nav.reportTemplates': 'Rapportskabeloner',
        'nav.alarmTemplates': 'Alarmskabeloner',
        'nav.settings': 'Indstillinger',
        'nav.logout': 'Log ud',
        'nav.language': 'Sprog',
        'nav.setup': 'Opsætning',
        'nav.myAssets': 'Mine aktiver',
        'nav.tasks': 'Opgaver (Beta)',
        'nav.signOut': 'Log ud',
        
        // Dashboard
        'dashboard.title': 'Dashboard',
        'dashboard.welcome': 'Velkommen',
        'dashboard.welcomeBack': 'Velkommen tilbage',
        'dashboard.stats': 'Statistik',
        'dashboard.recentActivity': 'Seneste Aktivitet',
        'dashboard.accountInfo': 'Kontooplysninger',
        'dashboard.totalAccounts': 'Konti',
        'dashboard.totalUsers': 'Brugere',
        'dashboard.totalLocations': 'Lokationer',
        'dashboard.accountHierarchy': 'Kontohierarki',
        'dashboard.alarms': 'Alarmer',
        'dashboard.loadingAccounts': 'Indlæser kontostruktur...',
        'dashboard.loadingAlarms': 'Indlæser alarmer...',
        'dashboard.at': 'hos',
        
        // Accounts
        'accounts.title': 'Konti',
        'accounts.add': 'Tilføj Ny Konto',
        'accounts.addButton': 'Tilføj Konto',
        'accounts.edit': 'Rediger Konto',
        'accounts.delete': 'Slet Konto',
        'accounts.name': 'Kontonavn',
        'accounts.parent': 'Overordnet Konto',
        'accounts.selectParent': 'Vælg Overordnet Konto',
        'accounts.noParent': 'Ingen Overordnet (Topniveau)',
        'accounts.description': 'Beskrivelse',
        'accounts.createSuccess': 'Konto oprettet',
        'accounts.updateSuccess': 'Konto opdateret',
        'accounts.deleteSuccess': 'Konto slettet',
        'accounts.createError': 'Kunne ikke oprette konto',
        'accounts.updateError': 'Kunne ikke opdatere konto',
        'accounts.deleteError': 'Kunne ikke slette konto',
        'accounts.deleteConfirm': 'Er du sikker på, at du vil slette kontoen',
        'accounts.deleteWarning': 'Dette vil også påvirke alle underkonti og tilknyttede data.',
        'accounts.hierarchy': 'Kontohierarki',
        'accounts.refresh': 'Opdater',
        'accounts.loading': 'Indlæser konti...',
        'accounts.directUsers': 'Direkte Brugere',
        'accounts.totalUsers': 'Total Brugere',
        'accounts.plan': 'Plan',
        'accounts.search': 'Søg Konti',
        'accounts.searchPlaceholder': 'Søg efter kontonavn...',
        'accounts.clear': 'Ryd',
        'accounts.createTitle': 'Opret Ny Konto',
        'accounts.accountType': 'Kontotype',
        'accounts.maxUsersOptional': 'Maks. brugere (Valgfri)',
        'accounts.maxUsersLabel': 'Maks. brugere',
        'accounts.maxUsersPlaceholder': 'Lad være tom for ubegrænset',
        'accounts.unlimited': 'Ubegrænset',
        'accounts.statusSuspended': 'Suspenderet',
        'accounts.noAccountsAvailable': 'Ingen konti tilgængelige. Dette bliver en topkonto.',
        'accounts.noAccountsAvailableShort': 'Ingen konti tilgængelige.',
        'accounts.errorLoadingAccounts': 'Fejl ved indlæsning af konti',
        'accounts.accountNamePlaceholder': 'Indtast kontonavn',
        'accounts.parentAccountDesc': 'Vælg hvor denne konto er placeret i hierarkiet. Lad være valgt for en topkonto.',
        'accounts.updateButton': 'Opdater Konto',
        'accounts.createButton': 'Opret Konto',
        'accounts.enableTimeTracking': 'Aktivér Tidsregistrering',
        'accounts.enableSmartTime': 'Aktivér SmartTime-brugere',
        'accounts.enableTimeBudget': 'Aktivér Tidsbudget',
        'accounts.topLevelFeaturesHint': 'Kun tilgængelig for topkonti direkte under Docospot. Slå funktioner fra for at forenkle lokationsformularen.',
        
        // Users
        'users.title': 'Brugere',
        'users.add': 'Tilføj Ny Bruger',
        'users.addButton': 'Tilføj Bruger',
        'users.edit': 'Rediger Bruger',
        'users.delete': 'Slet Bruger',
        'users.email': 'Email',
        'users.username': 'Brugernavn',
        'users.fullName': 'Fulde Navn',
        'users.role': 'Rolle',
        'users.status': 'Status',
        'users.account': 'Konto',
        'users.password': 'Adgangskode',
        'users.confirmPassword': 'Bekræft Adgangskode',
        'users.createSuccess': 'Bruger oprettet',
        'users.updateSuccess': 'Bruger opdateret',
        'users.deleteSuccess': 'Bruger slettet',
        'users.createError': 'Kunne ikke oprette bruger',
        'users.updateError': 'Kunne ikke opdatere bruger',
        'users.deleteError': 'Kunne ikke slette bruger',
        'users.deleteConfirm': 'Er du sikker på, at du vil slette brugeren',
        'users.deleteWarning': 'Denne handling kan ikke fortrydes.',
        'users.roleUser': 'Bruger',
        'users.roleAdmin': 'Administrator',
        'users.roleSuperAdmin': 'Superadministrator',
        'users.search': 'Søg Brugere',
        'users.searchPlaceholder': 'Søg efter navn, email...',
        'users.filterByAccount': 'Filtrer efter Konto',
        'users.filterByRole': 'Filtrer efter Rolle',
        'users.anyAccount': 'Enhver konto',
        'users.anyRole': 'Enhver rolle',
        'users.allRoles': 'Alle roller',
        'users.loadingAccounts': 'Indlæser konti...',
        'users.clearAll': 'Ryd alle',
        'users.clear': 'Ryd',
        'users.refresh': 'Opdater',
        'users.export': 'Eksporter',
        'users.loading': 'Indlæser brugere...',
        'users.totalCount': 'Totale brugere',
        'users.customizeColumns': '💡 Højreklik tabeloverskrift for at tilpasse kolonner',
        'users.allLoaded': 'alle indlæst',
        'users.employeeId': 'Medarbejder ID',
        'users.nfcCardId': 'NFC Kort ID',
        'users.nfcCardIdHint': 'Adskil flere kort med komma',
        'users.createTitle': 'Opret Ny Bruger',
        'users.phoneNumber': 'Telefonnummer',
        'users.department': 'Afdeling',
        'users.workAddress': 'Arbejdsadresse',
        'users.sendWelcomeEmail': 'Send velkomst-email med opsætningslink',
        'users.sendWelcomeEmailDesc': 'Brugeren modtager en email med et sikkert link til at oprette adgangskode. Linket udløber om 7 dage.',
        'users.resendWelcomeEmail': 'Send/Gensend velkomst-email',
        'users.grantAccess': 'Giv Adgang',
        'users.noSystemAccess': 'Brugere uden adgang kan ikke logge ind',
        'users.emailAlreadyRegistered': 'Denne email er allerede registreret',
        'users.createUserButton': 'Opret Bruger',
        'users.updateUserButton': 'Opdater Bruger',
        'users.noAccountsFound': 'Ingen konti fundet',
        'users.errorLoadingAccounts': 'Fejl ved indlæsning af konti',
        'users.selectAccountRequired': 'Vælg venligst en konto',
        'users.searchAccountsPlaceholder': 'Søg konti efter navn...',
        'users.users': 'brugere',
        
        // Locations
        'locations.title': 'Lokationer',
        'locations.add': 'Tilføj Ny Lokation',
        'locations.addButton': 'Tilføj Lokation',
        'locations.edit': 'Rediger Lokation',
        'locations.delete': 'Slet Lokation',
        'locations.name': 'Navn',
        'locations.address': 'Adresse',
        'locations.account': 'Konto',
        'locations.selectAccount': 'Vælg Konto',
        'locations.sensors': 'Tilknyt Sensorer',
        'locations.sensorsOptional': 'Tilknyt Sensorer (Valgfri)',
        'locations.sensorsHelp': 'Vælg sensorer, der skal tilknyttes denne lokation. Kun ikke-tilknyttede sensorer vises.',
        'locations.timeBudget': 'Tidsbudget',
        'locations.timeBudgetOptional': 'Tidsbudget (Valgfri)',
        'locations.timeBudgetPeriod': 'Periode',
        'locations.timeBudgetHours': 'Timer',
        'locations.timeBudgetHoursOptional': 'Timer (Valgfri)',
        'locations.weekly': 'Ugentlig',
        'locations.monthly': 'Månedlig',
        'locations.createSuccess': 'Lokation oprettet',
        'locations.updateSuccess': 'Lokation opdateret',
        'locations.deleteSuccess': 'Lokation slettet',
        'locations.createError': 'Kunne ikke oprette lokation',
        'locations.updateError': 'Kunne ikke opdatere lokation',
        'locations.deleteError': 'Kunne ikke slette lokation',
        'locations.deleteConfirm': 'Er du sikker på, at du vil slette lokationen',
        'locations.deleteWarning': 'Denne handling kan ikke fortrydes.',
        'locations.requiredFields': 'Udfyld venligst alle påkrævede felter',
        'locations.search': 'Søg Lokationer',
        'locations.searchPlaceholder': 'Søg efter navn, adresse...',
        
        // Assets
        'assets.title': 'Aktiver',
        'assets.subtitle': 'Administrer dine sporede aktiver',
        'assets.create': 'Opret Aktiv',
        'assets.createAsset': 'Opret Nyt Aktiv',
        'assets.editAsset': 'Rediger Aktiv',
        'assets.searchPlaceholder': 'Søg aktiver...',
        'assets.noAssets': 'Ingen aktiver fundet',
        'assets.noAssetsDescription': 'Kom i gang ved at oprette dit første aktiv',
        'assets.assetId': 'Aktiv ID',
        'assets.assetIdPlaceholder': 'Indtast unik aktiv ID',
        'assets.assetIdHelp': 'Unik identifikator for dette aktiv (f.eks. ASSET-001, VEH-123)',
        'assets.assetIdRequired': 'Aktiv ID er påkrævet',
        'assets.name': 'Aktiv Navn',
        'assets.namePlaceholder': 'Indtast aktiv navn',
        'assets.homeLocation': 'Hjemmelokation',
        'assets.homeLocationOptional': 'Hjemmelokation (Valgfri)',
        'assets.noHomeLocation': 'Ingen hjemmelokation',
        'assets.homeLocationHelp': 'Valgfri: Standard lokation for dette aktiv',
        'assets.sensors': 'Tilknyttede Sensorer',
        'assets.sensorsOptional': 'Tilknyttede Sensorer (Valgfri)',
        'assets.sensorsHelp': 'Vælg sensorer, der er tilknyttet dette aktiv',
        'assets.selectAccountFirst': 'Vælg først en konto for at se tilgængelige sensorer',
        'assets.search': 'Søg',
        'assets.searchAccounts': 'Søg konti...',
        'assets.filterByAccount': 'Filtrer efter Konto',
        'assets.anyAccount': 'Enhver konto',
        'assets.loadingAccounts': 'Indlæser konti...',
        'assets.clearAll': 'Ryd alle',
        'assets.clear': 'Ryd',
        'assets.refresh': 'Opdater',
        'assets.account': 'Konto',
        'assets.accountCannotChange': 'Konto kan ikke ændres ved redigering',
        'assets.nameRequired': 'Aktiv navn er påkrævet',
        'assets.accountRequired': 'Konto er påkrævet',
        'assets.createSuccess': 'Aktiv oprettet',
        'assets.updateSuccess': 'Aktiv opdateret',
        'assets.deleteSuccess': 'Aktiv slettet',
        'assets.deleteAsset': 'Slet Aktiv',
        'assets.deleteConfirm': 'Er du sikker på, at du vil slette "{name}"? Denne handling kan ikke fortrydes.',
        'locations.filterByAccount': 'Filtrer efter Konto',
        'locations.anyAccount': 'Enhver konto',
        'locations.loadingAccounts': 'Indlæser konti...',
        'locations.clearAll': 'Ryd alle',
        'locations.clear': 'Ryd',
        'locations.refresh': 'Opdater',
        'locations.export': 'Eksporter',
        'locations.loading': 'Indlæser lokationer...',
        'locations.allLoaded': 'alle indlæst',
        'locations.locations': 'lokationer',
        'locations.location': 'Lokation',
        'locations.sensors': 'Sensorer',
        'locations.mapDescription': 'Flyt kortet for at se lokationer i forskellige områder',
        'locations.nearbyLocations': 'Lokationer i nærheden',
        'locations.distance': 'Afstand',
        'locations.latestLog': 'Seneste Log',
        'locations.noActivity': 'Ingen aktivitet',
        'locations.noLocationsFound': 'Ingen lokationer fundet',
        'locations.checkIn': 'Check Ind',
        'locations.checkOut': 'Check Ud',
        'locations.createTitle': 'Opret Ny Lokation',
        'locations.locationName': 'Lokationsnavn',
        'locations.searchAccountsPlaceholder': 'Søg konti efter navn...',
        'locations.geocodeAddress': 'Find koordinater',
        'locations.coordinates': 'Koordinater',
        'locations.latitude': 'Breddegrad',
        'locations.longitude': 'Længdegrad',
        'locations.autoFilledGeocoding': 'Udfyldes automatisk ved geocoding',
        'locations.coordinatesHint': 'Valgfri: Koordinater til kortvisning',
        'locations.sensorsLabel': 'Sensorer',
        'locations.loadingSensors': 'Indlæser sensorer...',
        'locations.selectSensorsHelp': 'Vælg sensorer til at tilknytte denne lokation',
        'locations.externalContacts': 'Eksterne Kontakter (Valgfri)',
        'locations.loadingUsers': 'Indlæser brugere...',
        'locations.selectExternalContactsHelp': 'Vælg eksterne kontaktbrugere til denne lokation',
        'locations.smarttimeUsers': 'SmartTime Brugere (Valgfri)',
        'locations.searchUsersToAdd': 'Søg brugere at tilføje...',
        'locations.noUsersAdded': 'Ingen brugere tilføjet',
        'locations.smarttimeDesc': 'Tilføj brugere og sæt forventede daglige timer',
        'locations.smarttimeConfigured': 'SmartTime konfigureret',
        'locations.smarttimeUserCount': '{count} SmartTime-bruger(e) konfigureret',
        'locations.smarttimeNotConfigured': 'Ingen SmartTime konfigureret',
        'locations.budgetPeriod': 'Budgetperiode',
        'locations.daily': 'Daglig',
        'locations.budgetHours': 'Budgettimer',
        'locations.budgetHoursDesc': 'Timer tildelt per periode',
        'locations.createButton': 'Opret Lokation',
        'locations.updateButton': 'Opdater Lokation',
        'locations.noAccountsFound': 'Ingen konti fundet',
        'locations.selectAccountRequired': 'Vælg venligst en konto',
        'locations.smarttimeHoursRequired': 'Vælg venligst forventede daglige timer for alle valgte SmartTime brugere',
        'locations.massAlarm.assignTitle': 'Tildel alarm til lokationer',
        'locations.massAlarm.assignDescription': 'Tildel den valgte alarmskabelon til alle sensorer på tværs af {count} lokation(er).',
        'locations.massAlarm.assignConfirm': 'Tildel',
        'locations.massAlarm.removeTitle': 'Fjern alarmer fra lokationer',
        'locations.massAlarm.removeDescription': 'Fjern alarmtildelinger fra alle sensorer på tværs af {count} lokation(er).',
        'locations.massAlarm.removeConfirm': 'Fjern',
        'locations.massAlarm.suspendTitle': 'Pause alarmer for lokationer',
        'locations.massAlarm.suspendDescription': 'Sæt midlertidigt alarmtildelinger på pause på tværs af {count} lokation(er).',
        'locations.massAlarm.suspendDays': 'Pause i (dage)',
        'locations.massAlarm.suspendConfirm': 'Pause',
        'locations.massAlarm.invalidDays': 'Angiv et antal dage mellem 1 og 365',
        'locations.massAlarm.resumeTitle': 'Genoptag alarmer for lokationer',
        'locations.massAlarm.resumeDescription': 'Fjern pause på alarmtildelinger på tværs af {count} lokation(er).',
        'locations.massAlarm.resumeConfirm': 'Genoptag',
        'locations.massAlarm.template': 'Alarmskabelon',
        'locations.massAlarm.selectTemplate': 'Vælg en alarmskabelon…',
        'locations.massAlarm.allTemplates': 'Alle alarmskabeloner',
        'locations.massAlarm.templateRequired': 'Vælg venligst en alarmskabelon',
        'locations.massAlarm.noSelection': 'Ingen lokationer valgt',
        'locations.massAlarm.loadTemplatesError': 'Kunne ikke indlæse alarmskabeloner',
        'locations.massAlarm.created': 'Oprettet',
        'locations.massAlarm.removed': 'Fjernet',
        'locations.massAlarm.updated': 'Opdateret',
        'locations.massAlarm.skipped': 'Sprunget over',
        'locations.massAlarm.incompatible': 'Forkert sensortype',
        'locations.massAlarm.noSensors': 'De valgte lokationer har ingen sensorer.',
        'locations.massAlarm.noMatchingTemplates': 'Ingen alarmskabeloner passer til sensortyperne i de valgte lokationer ({types}).',
        'locations.massAlarm.locations': 'lokation(er)',
        'locations.massAlarm.sensors': 'sensor(er)',
        
        // Sensors
        'sensors.title': 'Sensorer',
        'sensors.add': 'Tilføj Ny Sensor',
        'sensors.addButton': 'Tilføj Sensor',
        'sensors.importButton': 'Importér Sensorer',
        'sensors.importTitle': 'Importér Sensorer',
        'sensors.importDescription': 'Masseimport af sensorer fra en Xtellio Excel-pakkefil.',
        'sensors.importFile': 'Excel-fil',
        'sensors.importFileHint': 'Vælg en .xlsx-fil, der indeholder en "serial_number"-kolonne.',
        'sensors.importSubmit': 'Importér',
        'sensors.importing': 'Importerer...',
        'sensors.importRequiredError': 'Vælg venligst en fil, sensortype og konto.',
        'sensors.importSuccess': 'Importerede {count} sensor(er).',
        'sensors.importError': 'Import af sensorer mislykkedes',
        'sensors.importResultImported': 'Importeret',
        'sensors.importResultSkipped': 'Sprunget over (findes allerede)',
        'sensors.edit': 'Rediger Sensor',
        'sensors.delete': 'Slet Sensor',
        'sensors.sensorId': 'Sensor ID',
        'sensors.name': 'Sensornavn',
        'sensors.type': 'Type',
        'sensors.battery': 'Batteristatus',
        'sensors.location': 'Lokation',
        'sensors.asset': 'Aktiv',
        'sensors.account': 'Konto',
        'sensors.status': 'Status',
        'sensors.lastSeen': 'Sidst Set',
        'sensors.lastData': 'Sidste Data',
        'sensors.batteryLevel': 'Batteriniveau (mV)',
        'sensors.search': 'Søg Sensorer',
        'sensors.searchPlaceholder': 'Søg efter navn, ID...',
        'sensors.filterByAccount': 'Filtrer efter Konto',
        'sensors.filterByBattery': 'Filtrer efter Batteristatus',
        'sensors.anyAccount': 'Enhver konto',
        'sensors.allStatuses': 'Alle statusser',
        'sensors.loadingAccounts': 'Indlæser konti...',
        'sensors.clearAll': 'Ryd alle',
        'sensors.clear': 'Ryd',
        'sensors.refresh': 'Opdater',
        'sensors.export': 'Eksporter',
        'sensors.loading': 'Indlæser sensorer...',
        'sensors.totalCount': 'Totale sensorer',
        'sensors.customizeColumns': '💡 Højreklik tabeloverskrift for at tilpasse kolonner',
        'sensors.selected': 'valgt',
        'sensors.clearSelection': 'Ryd',
        'sensors.bulkAssignAccount': 'Tildel til konto',
        'sensors.bulkAssignDescription': 'Flyt {count} valgte sensor(er) til kontoen nedenfor. Enhver lokations- eller aktivtilknytning fjernes.',
        'sensors.bulkAssignConfirm': 'Tildel',
        'sensors.selectAccountRequired': 'Vælg venligst en konto.',
        'sensors.bulkUpdated': 'Opdateret',
        'sensors.bulkSkipped': 'Sprunget over',
        'sensors.createSuccess': 'Sensor oprettet',
        'sensors.updateSuccess': 'Sensor opdateret',
        'sensors.deleteSuccess': 'Sensor slettet',
        'sensors.createError': 'Kunne ikke oprette sensor',
        'sensors.updateError': 'Kunne ikke opdatere sensor',
        'sensors.deleteError': 'Kunne ikke slette sensor',
        'sensors.deleteConfirm': 'Er du sikker på, at du vil slette sensoren',
        'sensors.deleteWarning': 'Denne handling kan ikke fortrydes.',
        'sensors.batteryGood': 'God',
        'sensors.batteryLow': 'Lav',
        'sensors.batteryCritical': 'Kritisk',
        'sensors.batteryRed': '🔴 Rød (Kritisk)',
        'sensors.batteryYellow': '🟡 Gul (Advarsel)',
        'sensors.batteryGreen': '🟢 Grøn (God)',
        'sensors.batteryNoData': '⚪ Ingen data',
        'sensors.unassociated': 'Ikke-tilknyttet',
        'sensors.accountSelected': 'konto valgt',
        'sensors.accountsSelected': 'konti valgt',
        'sensors.showing': 'Viser',
        'sensors.of': 'af',
        'sensors.sensorsLowercase': 'sensorer',
        'sensors.scrollForMore': 'rul for mere',
        'sensors.total': 'Total',
        'sensors.showingFirst': 'viser første',
        'sensors.allLoaded': 'alle indlæst',
        'sensors.accountFilter': 'kontofilter',
        'sensors.sensorType': 'Sensortype',
        'sensors.selectSensorType': 'Vælg sensortype',
        'sensors.typeTemperature': 'Temperatur',
        'sensors.typeTempHumidity': 'Temperatur/Luftfugtighed/Tryk',
        'sensors.typeTempHumidityBle': 'Temperatur/Luftfugtighed',
        'sensors.typeTracker': 'Tracker',
        'sensors.typeGateway': 'Gateway',
        'sensors.typeCardReader': 'Kortlæser',
        'sensors.typeActivity': 'Aktivitet',
        'sensors.typeLight': 'Lys',
        'sensors.macAddress': 'BLE MAC-adresse',
        'sensors.macAddressHint': 'Påkrævet for at modtage data, når gatewayen kører i Beacon-tilstand. MAC-adressen identificerer denne BLE-sensor entydigt.',
        'sensors.statusMaintenance': 'Vedligehold',
        'sensors.createButton': 'Opret Sensor',
        'sensors.logsTitle': 'Sensorlogfiler',
        'sensors.recentLogs': 'Seneste logfiler',
        'sensors.noLogsFound': 'Ingen logfiler fundet',
        'sensors.noLogsForDateRange': 'Ingen logfiler fundet for valgt datointerval',
        'sensors.rawJsonData': 'Rå JSON Data',
        'sensors.selectLogToView': 'Vælg en logfil for at se detaljer',
        'sensors.noAccountsFound': 'Ingen konti fundet',
        'sensors.errorLoadingAccounts': 'Fejl ved indlæsning af konti',
        'sensors.errorLoadingLogs': 'Kunne ikke indlæse sensorlogfiler',
        'sensors.requiredFieldsError': 'Udfyld venligst alle påkrævede felter (Navn, ID, Type og Konto er obligatoriske)',
        
        // Assets
        'assets.title': 'Aktiver',
        'assets.add': 'Tilføj Nyt Aktiv',
        'assets.edit': 'Rediger Aktiv',
        'assets.delete': 'Slet Aktiv',
        'assets.name': 'Aktivnavn',
        'assets.type': 'Type',
        'assets.account': 'Konto',
        'assets.description': 'Beskrivelse',
        'assets.serialNumber': 'Serienummer',
        'assets.location': 'Lokation',
        'assets.sensors': 'Tilknyttede Sensorer',
        'assets.createSuccess': 'Aktiv oprettet',
        'assets.updateSuccess': 'Aktiv opdateret',
        'assets.deleteSuccess': 'Aktiv slettet',
        'assets.createError': 'Kunne ikke oprette aktiv',
        'assets.updateError': 'Kunne ikke opdatere aktiv',
        'assets.deleteError': 'Kunne ikke slette aktiv',
        'assets.deleteConfirm': 'Er du sikker på, at du vil slette aktivet',
        'assets.deleteWarning': 'Denne handling kan ikke fortrydes.',
        
        // Groups
        'groups.title': 'Grupper',
        'groups.add': 'Tilføj Ny Gruppe',
        'groups.edit': 'Rediger Gruppe',
        'groups.delete': 'Slet Gruppe',
        'groups.name': 'Gruppenavn',
        'groups.description': 'Beskrivelse',
        'groups.members': 'Medlemmer',
        'groups.account': 'Konto',
        'groups.createSuccess': 'Gruppe oprettet',
        'groups.updateSuccess': 'Gruppe opdateret',
        'groups.deleteSuccess': 'Gruppe slettet',
        'groups.createError': 'Kunne ikke oprette gruppe',
        'groups.updateError': 'Kunne ikke opdatere gruppe',
        'groups.deleteError': 'Kunne ikke slette gruppe',
        'groups.deleteConfirm': 'Er du sikker på, at du vil slette gruppen',
        'groups.deleteWarning': 'Denne handling kan ikke fortrydes.',
        
        // Time Tracking
        'time.title': 'Tidsregistrering',
        'time.add': 'Tilføj Tidsregistrering',
        'time.edit': 'Rediger Tidsregistrering',
        'time.delete': 'Slet Tidsregistrering',
        'time.startTime': 'Starttid',
        'time.endTime': 'Sluttid',
        'time.duration': 'Varighed',
        'time.location': 'Lokation',
        'time.user': 'Bruger',
        'time.sensor': 'Sensor',
        'time.notes': 'Noter',
        'time.date': 'Dato',
        'time.hours': 'Timer',
        'time.createSuccess': 'Tidsregistrering oprettet',
        'time.updateSuccess': 'Tidsregistrering opdateret',
        'time.deleteSuccess': 'Tidsregistrering slettet',
        'time.createError': 'Kunne ikke oprette tidsregistrering',
        'time.updateError': 'Kunne ikke opdatere tidsregistrering',
        'time.deleteError': 'Kunne ikke slette tidsregistrering',
        'time.deleteConfirm': 'Er du sikker på, at du vil slette denne tidsregistrering',
        'time.deleteEvent': 'Slet Tidsregistrering',
        'time.deleteWarning': 'Denne handling kan ikke fortrydes.',
        'time.copyEvent': 'Kopier Tidsregistrering',
        'time.copyConfirm': 'Er du sikker på, at du vil oprette en kopi af denne tidsregistrering?',
        'time.copySuccess': 'Tidsregistrering kopieret',
        'time.editEvent': 'Rediger Tidsregistrering',
        'time.eventUpdatedSuccess': 'Tidsregistrering opdateret',
        'time.filterByLocation': 'Filtrer efter Lokation',
        'time.filterByUser': 'Filtrer efter Bruger',
        'time.filterByDate': 'Filtrer efter Dato',
        'time.totalHours': 'Total Timer',
        'time.thisWeek': 'Denne Uge',
        'time.thisMonth': 'Denne Måned',
        'time.selectLocation': 'Vælg Lokation',
        'time.selectUser': 'Vælg Bruger',
        'time.allLocations': 'Alle Lokationer',
        'time.allUsers': 'Alle Brugere',
        'time.dateRange': 'Dato',
        'time.dateFrom': 'Fra Dato',
        'time.dateTo': 'Til Dato',
        'time.to': 'til',
        'time.status': 'Status',
        'time.allStatuses': 'Alle',
        'time.completed': 'Afsluttet',
        'time.open': 'Åben',
        'time.modified': 'Ændret',
        'time.manual': 'Manuel',
        'time.currentValue': 'Nuværende Værdi',
        'time.newTime': 'Ny Tid',
        'time.reason': 'Årsag',
        'time.reasonPlaceholder': 'f.eks., Glemte at tjekke ud, Justeret for pause',
        'time.changeLog': 'Ændringslog',
        'time.totalChanges': 'Total Ændringer',
        'time.editedAt': 'Redigeret',
        'time.editedBy': 'Redigeret Af',
        'time.change': 'Ændring',
        'time.oldCheckIn': 'Gammel Ind',
        'time.oldCheckOut': 'Gammel Ud',
        'time.newCheckIn': 'Ny Ind',
        'time.newCheckOut': 'Ny Ud',
        'time.noEditHistory': 'Ingen redigeringshistorik tilgængelig',
        'time.manualCreation': 'Manuel Oprettelse',
        'time.checkInEdit': 'Ind Redigering',
        'time.checkOutEdit': 'Ud Redigering',
        'time.addEvent': 'Tilføj Registrering',
        'time.searchUser': 'Søg Bruger',
        'time.searchUserPlaceholder': 'Søg efter navn, email...',
        'time.account': 'Konto',
        'time.allAccounts': 'Alle Konti',
        'time.searchLocations': 'Søg lokationer...',
        'time.loadingEvents': 'Indlæser tidsregistrering...',
        'time.userName': 'Brugernavn',
        'time.employee': 'Medarbejder',
        'time.events': 'registreringer',
        'time.noEventsFound': 'Ingen registreringer fundet',
        'time.checkIn': 'Tjek Ind',
        'time.checkOut': 'Tjek Ud',
        'time.checkInTime': 'Tjek Ind',
        'time.checkOutTime': 'Tjek Ud',
        'time.totalTime': 'Samlet Tid',
        'time.actions': 'Handlinger',
        'time.timeBudget': 'Tidsbudget',
        'time.budgetDaily': 'dag',
        'time.budgetWeekly': 'uge',
        'time.budgetMonthly': 'måned',
        'time.timeTracking': 'Tidsregistrering',
        'time.viewBy': 'Vis efter',
        'time.weekly': 'Ugentlig',
        'time.monthly': 'Månedlig',
        'time.daily': 'Daglig',
        'time.selectPeriods': 'Vælg Perioder',
        'time.onlyShowPeriodsWithActivity': 'Vis kun perioder med aktivitet',
        'time.budgetStatsDaysWithRegistration': 'Dage med registrering',
        'time.budgetStatsTotalTime': 'Total tid',
        'time.budgetStatsAvgPerDay': 'Gennemsnit pr. dag',
        'time.addManualEvent': 'Tilføj Manuel Begivenhed',
        'time.checkInRequired': 'Indtjekningstid er påkrævet',
        'time.selectUserRequired': 'Vælg venligst en bruger',
        'time.selectLocationRequired': 'Vælg venligst et sted',
        'time.eventCreatedSuccess': 'Tidssporingsbegivenhed oprettet',
        'time.eventCreateError': 'Kunne ikke oprette begivenhed',
        'time.userPlaceholder': 'Søg og vælg en bruger...',
        'time.locationPlaceholder': 'Søg og vælg et sted...',
        'time.viewMode': 'Visning',
        'time.current': 'Nuværende',
        'time.original': 'Oprindelig',
        'time.unknownCard': 'Ukendt Kort',
        'time.unknownCardMessage': 'Dette kort er ikke tildelt nogen bruger. Kopier kort-ID\'et nedenfor og tildel det til den korrekte bruger i Brugersektionen.',
        'time.cardId': 'Kort-ID',
        'time.cardIdCopied': 'Kort-ID kopieret til udklipsholder',
        'time.copyFailed': 'Kunne ikke kopiere',
        'time.howToAssignCard': 'Sådan tildeler du dette kort:',
        'time.copyCardId': 'Kopier kort-ID\'et ovenfor',
        'time.goToUsers': 'Gå til Brugersektionen',
        'time.findUser': 'Find den korrekte bruger',
        'time.pasteCardId': 'Indsæt kort-ID\'et i feltet "NFC Kort-ID"',
        'time.saveUser': 'Gem brugeren, og klik derefter på den ukendte bruger igen for at opdatere navnet',
        'time.unknownUserFilter': 'Vis kun ukendte brugere',
        'time.unknownLocation': 'Ukendt Lokation',
        'time.unknownLocationMessage': 'Denne sensor er ikke tildelt nogen lokation. Kopier sensor-ID\'et nedenfor og tildel det i Opsætningssektionen.',
        'time.sensorId': 'Sensor-ID',
        'time.howToAssignSensor': 'Sådan tildeler du denne sensor:',
        'time.copySensorId': 'Kopier sensor-ID\'et ovenfor',
        'time.goToSetup': 'Gå til Opsætningssektionen',
        'time.findSensor': 'Find denne sensor (du kan indsætte ID\'et i søgningen)',
        'time.assignLocation': 'Tildel sensoren til den korrekte lokation',
        'time.saveLocation': 'Gem konfigurationen, og klik derefter på den ukendte lokation igen for at opdatere',
        'time.noChanges': 'Ingen ændringer foretaget',
        'time.eventNotFound': 'Begivenhed ikke fundet',
        'time.enterAtLeastOneTime': 'Indtast venligst mindst ét tidspunkt',
        
        // Settings
        'settings.title': 'Indstillinger',
        'settings.description': 'Konfigurer programindstillinger og præferencer',
        'settings.timeTracking': 'Tidsregistrering Indstillinger',
        'settings.timeTrackingDescription': 'Kontroller synlighed af tidsregistreringer for eksterne kontakter',
        'settings.allowExternal': 'Tillad eksterne kontakter at se tidsregistreringer',
        'settings.allowExternalDescription': 'Når aktiveret kan eksterne kontakter se tidsregistreringer. Brug datobegrænsningen nedenfor til at kontrollere hvilke registreringer de kan se.',
        'settings.restrictDate': 'Begræns registreringer op til en specifik dato',
        'settings.restrictDateDescription': 'Vis kun registreringer op til og inklusive den valgte dato',
        'settings.showLogsUpTo': 'Vis registreringer op til denne dato',
        'settings.noRestriction': 'Ingen begrænsning - vis alle registreringer',
        'settings.delayDays': 'Forsink adgang med antal dage',
        'settings.delayDaysDescription': 'Vis kun registreringer der er ældre end det angivne antal dage',
        'settings.delayDaysLabel': 'Adgangsforsinkelse (dage)',
        'settings.accessRestrictionType': 'Adgangsbegrænsning',
        'settings.startDateRestriction': 'Skjul registreringer før en bestemt dato',
        'settings.startDateRestrictionDescription': 'Skjul registreringer ældre end den valgte dato for eksterne kontakter (f.eks. hvis tidlige data er upålidelige).',
        'settings.startDateLabel': 'Vis registreringer fra denne dato',
        'settings.smartTimeTitle': 'SmartTime indstillinger',
        'settings.smartTimeMinDeviation': 'Minimum afvigelse fra forventet tid (minutter)',
        'settings.smartTimeRandomDeviation': 'Tilfældigt afvigelsesinterval (minutter)',
        'settings.smartTimeDescription': 'SmartTime justerer til forventet tid minus minimum afvigelse plus et tilfældigt tal inden for intervallet.',
        'settings.timeBudgetTitle': 'Tidsbudget indstillinger',
        'settings.timeBudgetRedLimitBelow': 'Rød grænse under budget (minutter)',
        'settings.timeBudgetRedLimitAbove': 'Rød grænse over budget (minutter)',
        'settings.timeBudgetRedLimitPlaceholder': 'Lad feltet stå tomt for ingen grænse',
        'settings.timeBudgetDescription': 'Rækker er grønne tæt på budgettet og røde når der er for langt fra. Angiv antal minutter under og/eller over budget hvor en række bliver rød. Lad et felt stå tomt for aldrig at blive rød på den side.',
        'settings.saveSettings': 'Gem Indstillinger',
        'settings.lastUpdated': 'Sidst opdateret',
        'settings.by': 'af',
        'settings.reportTemplates': 'Rapportskabelon Indstillinger',
        'settings.reportTemplatesDescription': 'Konfigurer standard rapportskabeloner for forskellige funktioner',
        'settings.timeTrackingReportTemplate': 'Standard Tidsregistrering Rapportskabelon',
        'settings.timeTrackingReportTemplateDescription': 'Vælg en Filter rapportskabelon til hurtig PDF-generering fra Tidsregistrering',
        'settings.environmentalReportTemplate': 'Standard Sensorrapport Skabelon (Temp/Fugt)',
        'settings.environmentalReportTemplateDescription': 'Vælg en Filter rapportskabelon til hurtig PDF-generering fra temperatur/fugtigheds-sensordata',
        'insights.exportExcel': 'Eksportér til Excel',
        'insights.exportPdf': 'PDF',
        'insights.pdfOptionsTitle': 'PDF Eksportindstillinger',
        'insights.sortBy': 'Sortér data efter',
        'insights.dateNewestFirst': 'Dato (nyeste først)',
        'insights.dateOldestFirst': 'Dato (ældste først)',
        'insights.includeGraphs': 'Inkludér grafer',
        'insights.includeTable': 'Inkludér datapunkt-tabel',
        'insights.generatePdf': 'Generér PDF',
        'insights.generatingPdf': 'Genererer PDF-rapport...',
        'insights.sensorReport': 'Sensorrapport',
        'settings.noneAskEveryTime': 'Ingen - Spørg hver gang',
        
        // Report Templates
        'reportTemplates.title': 'Rapportskabeloner',
        'reportTemplates.addTemplate': 'Tilføj Skabelon',
        'reportTemplates.copyTemplate': 'Kopier Skabelon',
        'reportTemplates.searchPlaceholder': 'Søg skabeloner...',
        'reportTemplates.loadingTemplates': 'Indlæser skabeloner...',
        'reportTemplates.selectTemplate': 'Vælg en skabelon',
        'reportTemplates.selectTemplateSubtitle': 'Vælg en skabelon fra venstre eller opret en ny',
        'reportTemplates.delete': 'Slet',
        'reportTemplates.save': 'Gem',
        'reportTemplates.release': 'Frigiv',
        'reportTemplates.preview': 'Forhåndsvisning',
        'reportTemplates.frontPage': 'Forside',
        'reportTemplates.results': 'Resultater',
        'reportTemplates.documentation': 'Dokumentation',
        'reportTemplates.finalRemarks': 'Afsluttende Bemærkninger',
        'reportTemplates.noTemplateSelected': 'Ingen skabelon valgt',
        'reportTemplates.getStarted': 'Kom i gang med at oprette en ny rapportskabelon',
        'reportTemplates.standardTexts': 'Standardtekster',
        'reportTemplates.tags': 'Tags',
        'reportTemplates.selectReportType': 'Vælg Rapporttype',
        'reportTemplates.chooseTemplateType': 'Vælg den type rapportskabelon du vil oprette',
        'reportTemplates.dataCollection': 'Dataindsamling',
        'reportTemplates.dataCollectionDesc': 'Indsaml data med billeder, evalueringer og detaljeret dokumentation. Inkluderer struktureret eller dynamisk layoutmuligheder.',
        'reportTemplates.filterReport': 'Filterrapport',
        'reportTemplates.filterReportDesc': 'Søg og filtrer eksisterende data for at generere rapporter. Simpel forside og resultat layout.',
        'reportTemplates.statisticsReport': 'Statistikrapport',
        'reportTemplates.statisticsReportDesc': 'Generer statistisk analyse og visualiseringer. Simpel forside og resultat layout.',
        'reportTemplates.cancel': 'Annuller',
        'reportTemplates.statisticsComingSoon': 'Statistikrapporter kommer i en fremtidig opdatering. Vælg venligst Dataindsamling eller Filter indtil videre.',
        
        // Standard Texts
        'standardTexts.title': 'Standardtekster',
        'standardTexts.description': 'Organiser genanvendelige tekster i kategorier',
        'standardTexts.addCategory': 'Tilføj Kategori',
        'standardTexts.saveAll': 'Gem Alle',
        'standardTexts.saved': 'Gemt!',
        'standardTexts.noCategories': 'Ingen kategorier endnu',
        'standardTexts.clickToStart': 'Klik på "Tilføj Kategori" for at begynde at organisere dine standardtekster',
        
        // Tags
        'tags.title': 'Rapport Tags',
        'tags.description': 'Opret tag kategorier og tags til organisering af rapporter og statistik',
        'tags.addCategory': 'Tilføj Kategori',
        'tags.saveAll': 'Gem Alle',
        'tags.saved': 'Gemt!',
        
        // Asset Insights
        'assets.dataView': 'Data Visning',
        'assets.mapView': 'Kort Visning',
        'assets.searchAssets': 'Søg Aktiver',
        'assets.searchByName': 'Søg efter aktivnavn...',
        'assets.loadingAssets': 'Indlæser aktiver...',
        'assets.selectAsset': 'Vælg et aktiv for at se aktivitetsdata',
        'assets.failedToLoad': 'Kunne ikke indlæse aktiver',
        'assets.noAssetsFound': 'Ingen aktiver fundet',
        'assets.unnamedAsset': 'Unavngivet Aktiv',
        'assets.24h': '24t',
        'assets.daily': 'Daglig',
        'assets.threeDays': '3 Dage',
        'assets.weekly': 'Ugentlig',
        'assets.monthly': 'Månedlig',
        'assets.totalActivityAllTime': 'Total Aktivitet (Hele Perioden)',
        'assets.activitySelectedPeriod': 'Aktivitet for Valgt Periode',
        'assets.readings': 'aflæsninger',
        'assets.view': 'visning',
        'assets.mapViewTitle': 'Kort Visning',
        'assets.mapViewDescription': 'GPS-sporing og lokalitetshistorik vil blive vist her.',
        'assets.comingSoon': 'Kommer snart...',
        
        // Alarms
        'alarms.title': 'Alarmer',
        'alarms.activeAlarms': 'Aktive Alarmer',
        'alarms.history': 'Historik',
        'alarms.noActiveAlarms': 'Ingen aktive alarmer',
        'alarms.allAcknowledged': 'Alle alarmer er blevet kvitteret.',
        'alarms.noHistory': 'Ingen alarm historik',
        'alarms.acknowledgedAppear': 'Kvitterede alarmer vises her.',
        'alarms.type': 'Type',
        'alarms.time': 'Tid',
        'alarms.location': 'Lokation',
        'alarms.sensorName': 'Sensor Navn',
        'alarms.info': 'Info',
        'alarms.action': 'Handling',
        'alarms.acknowledged': 'Kvitteret',
        'alarms.notes': 'Noter',
        'alarms.acknowledge': 'Kvitter',
        'alarms.addNote': 'Tilføj note',
        'alarms.lastRegistration': 'Sidste registrering',
        'alarms.acknowledgeSuccess': 'Alarm kvitteret',
        'alarms.acknowledgeWithNoteSuccess': 'Alarm kvitteret med note',
        'alarms.acknowledgeError': 'Kunne ikke kvittere alarm',
        'alarms.loadingError': 'Fejl ved indlæsning af alarmer. Prøv igen.',
        'alarms.loadingActiveError': 'Fejl ved indlæsning af aktive alarmer. Prøv igen.',
        'alarms.loadingHistoryError': 'Fejl ved indlæsning af alarm historik. Prøv igen.',
        'alarms.addNoteTitle': 'Tilføj Note',
        'alarms.addNotePrompt': 'Tilføj en valgfri note til denne kvittering:',
        'alarms.notesPlaceholder': 'Indtast noter her...',
        'alarms.acknowledgeWithNote': 'Kvitter med Note',
        'alarms.noRegistrationFound': 'Ingen registrering fundet for sensor',
        
        // Alarm Templates
        'alarmTemplates.title': 'Alarm Skabeloner',
        'alarmTemplates.description': 'Opret og administrer genanvendelige alarm skabeloner, der kan tildeles flere sensorer.',
        'alarmTemplates.templates': 'Skabeloner',
        'alarmTemplates.filterByType': 'Filtrer efter type:',
        'alarmTemplates.allTypes': 'Alle Typer',
        'alarmTemplates.createTemplate': 'Opret Skabelon',
        'alarmTemplates.loadingTemplates': 'Indlæser skabeloner...',
        'alarmTemplates.noTemplatesFilter': 'Ingen skabeloner fundet for det valgte filter.',
        'alarmTemplates.tryDifferentFilter': 'Prøv at vælge en anden sensor type.',
        'alarmTemplates.noTemplatesYet': 'Ingen alarm skabeloner endnu.',
        'alarmTemplates.createFirstTemplate': 'Opret din første skabelon for at komme i gang.',
        'alarmTemplates.schedule': 'Tidsplan',
        'alarmTemplates.check': 'Tjek:',
        'alarmTemplates.max': 'Maks:',
        'alarmTemplates.days': 'Dage:',
        'alarmTemplates.noSchedules': 'Ingen tidsplaner konfigureret',
        'alarmTemplates.schedules': 'tidsplaner',
        'alarmTemplates.sensor': 'sensor',
        'alarmTemplates.sensors': 'sensorer',
        'alarmTemplates.smsRecipients': 'SMS Modtagere:',
        'alarmTemplates.users': 'bruger(e)',
        'alarmTemplates.smsMessage': 'SMS Besked:',
        'alarmTemplates.edit': 'Rediger',
        'alarmTemplates.delete': 'Slet',
        'alarmTemplates.cardReader': 'Kortlæser',
        'alarmTemplates.temperature': 'Temperatur',
        'alarmTemplates.temperatureHumidity': 'Temperatur/Fugtighed',
        'alarmTemplates.tracker': 'Tracker',
        'alarmTemplates.missingCheckin': 'Rapport over manglende check-in (kontoniveau)',
        'alarmTemplates.accounts': 'konti',
        'alarmTemplates.reportTime': 'Rapporttid:',
        'alarmTemplates.period': 'Periode:',
        'alarmTemplates.hours': 'timer',
        'alarmTemplates.emailRecipients': 'E-mail modtagere:',
        'alarmTemplates.manageAccounts': 'Administrer konti',
        'alarmTemplates.manageAccountsHelp': 'Vælg de konti alarmen gælder for. Alle lokationer under en valgt konto overvåges.',
        'alarmTemplates.testNow': 'Test nu',
        'alarmTemplates.sendTestEmailPrompt': 'Vil du sende denne rapport som e-mail til de valgte modtagere nu, som en test?',
        'alarmTemplates.sendTestEmail': 'Send test-e-mail',
        'alarmTemplates.testEmailSentTo': 'Test-e-mail sendt til modtagere:',
        'alarmTemplates.testEmailFailed': 'Kunne ikke sende test-e-mailen. Tjek at modtagerne har en gyldig e-mailadresse.',
        
        // Alarm Template Modal
        'alarmTemplates.modal.create': 'Opret Alarm Skabelon',
        'alarmTemplates.modal.edit': 'Rediger Alarm Skabelon',
        'alarmTemplates.modal.loading': 'Indlæser...',
        'alarmTemplates.modal.sensorType': 'Sensor Type *',
        'alarmTemplates.modal.selectSensorType': 'Vælg sensor type...',
        'alarmTemplates.modal.sensorTypeHelp': 'Denne alarm skabelon kan kun tildeles sensorer af denne type',
        'alarmTemplates.modal.sensorTypeNoChange': 'Sensor type kan ikke ændres efter oprettelse',
        'alarmTemplates.modal.selectSensorTypePrompt': 'Vælg venligst en sensor type for at konfigurere alarm indstillinger.',
        'alarmTemplates.modal.templateName': 'Skabelon Navn *',
        'alarmTemplates.modal.templateNamePlaceholder': 'f.eks., Daglig Check-In Alarm',
        'alarmTemplates.modal.description': 'Beskrivelse',
        'alarmTemplates.modal.descriptionPlaceholder': 'Valgfri beskrivelse af hvad denne skabelon er til',
        'alarmTemplates.modal.timezone': 'Tidszone *',
        'alarmTemplates.modal.timezoneHelp': 'Registreret fra din browser. Alarm tjek tider vil køre i denne tidszone.',
        'alarmTemplates.modal.timeSchedules': 'Tidsplaner *',
        'alarmTemplates.modal.schedulesRange': '(1-7 tidsplaner)',
        'alarmTemplates.modal.addSchedule': 'Tilføj Tidsplan',
        'alarmTemplates.modal.scheduleNumber': 'Tidsplan',
        'alarmTemplates.modal.checkTime': 'Tjek Tid *',
        'alarmTemplates.modal.maxTime': 'Maks Tid (minutter) *',
        'alarmTemplates.modal.activeDays': 'Aktive Dage',
        'alarmTemplates.modal.sendSMS': 'Send SMS notifikationer',
        'alarmTemplates.modal.smsRecipientsLabel': 'SMS Modtagere',
        'alarmTemplates.modal.noPhoneNumbers': 'Ingen brugere har telefonnumre konfigureret. Tilføj venligst telefonnumre til brugerprofiler for at aktivere SMS notifikationer.',
        'alarmTemplates.modal.usersWithoutPhone': 'bruger(e) uden telefonnumre vises ikke',
        'alarmTemplates.modal.holdCtrl': 'Hold Ctrl/Cmd for at vælge flere brugere',
        'alarmTemplates.modal.smsMessageLabel': 'SMS Besked (valgfri)',
        'alarmTemplates.modal.smsMessageHelp': 'Beskeden starter med lokationsnavn (eller sensornavn hvis ingen lokation). Lad stå tom for standard format.',
        'alarmTemplates.modal.smsMessagePlaceholder': 'f.eks., Ingen kort registrering registreret',
        'alarmTemplates.modal.sendTestSMS': 'Send Test SMS',
        'alarmTemplates.modal.testSMSHelp': 'Sender en test besked til valgte modtagere',
        'alarmTemplates.modal.comingSoon': 'Alarm Konfiguration Kommer Snart',
        'alarmTemplates.modal.comingSoonDesc': 'Alarm skabeloner for denne sensor type er endnu ikke tilgængelige. I øjeblikket understøtter kun Kortlæser sensorer alarm konfiguration.',
        'alarmTemplates.modal.cancel': 'Annuller',
        'alarmTemplates.modal.create': 'Opret Skabelon',
        'alarmTemplates.modal.update': 'Opdater Skabelon',
        'alarmTemplates.modal.maxSchedules': 'Maksimalt 7 tidsplaner tilladt per skabelon',
        'alarmTemplates.modal.missingCheckinIntro': 'Denne alarm kører én gang i døgnet for en hel konto og sender en e-mail med de lokationer, der ikke har haft check-in i den valgte periode. Kun lokationer med en NFC-kortlæser overvåges. Der sendes ingen e-mail, når alle lokationer har haft check-in.',
        'alarmTemplates.modal.reportTime': 'Rapporttid *',
        'alarmTemplates.modal.reportTimeHelp': 'Hvornår rapporten dannes, typisk midnat',
        'alarmTemplates.modal.lookbackHours': 'Periode (timer) *',
        'alarmTemplates.modal.lookbackHoursHelp': 'Hvor langt tilbage der kigges efter check-in, typisk 24',
        'alarmTemplates.modal.weekdays': 'Aktive dage',
        'alarmTemplates.modal.emailRecipientsLabel': 'E-mail modtagere *',
        'alarmTemplates.modal.emailRecipientsHelp': 'Teamledere og administratorer med en e-mailadresse. Hold Ctrl/Cmd nede for at vælge flere.',
        'alarmTemplates.modal.noEmailRecipients': 'Der blev ikke fundet teamledere eller administratorer med en e-mailadresse. Tilføj først en e-mailadresse på deres brugerprofil.',
        'alarmTemplates.modal.emailMessageLabel': 'Introtekst (valgfri)',
        'alarmTemplates.modal.emailMessagePlaceholder': 'f.eks., Følg venligst op på lokationerne nedenfor',
        'alarmTemplates.modal.templateNameRequired': 'Skabelon navn er påkrævet',
        'alarmTemplates.modal.reportTimeRequired': 'Rapporttid er påkrævet',
        'alarmTemplates.modal.lookbackHoursInvalid': 'Perioden skal være mellem 1 og 168 timer',
        'alarmTemplates.modal.emailRecipientsRequired': 'Vælg mindst én e-mail modtager',
        
        // Weekday abbreviations
        'weekdays.monday': 'Man',
        'weekdays.tuesday': 'Tir',
        'weekdays.wednesday': 'Ons',
        'weekdays.thursday': 'Tor',
        'weekdays.friday': 'Fre',
        'weekdays.saturday': 'Lør',
        'weekdays.sunday': 'Søn',
        'weekdays.mon': 'Ma',
        'weekdays.tue': 'Ti',
        'weekdays.wed': 'On',
        'weekdays.thu': 'To',
        'weekdays.fri': 'Fr',
        'weekdays.sat': 'Lø',
        'weekdays.sun': 'Sø',
    }
};

// Current language (default to browser language or English)
let currentLanguage = localStorage.getItem('language') || (navigator.language.startsWith('da') ? 'da' : 'en');

/**
 * Translation function - gets translated string for a key
 * @param {string} key - Translation key (e.g., 'common.save')
 * @returns {string} Translated string or the key itself if not found
 */
export function t(key) {
    return translations[currentLanguage]?.[key] || translations['en'][key] || key;
}

/**
 * Helper function to translate backend-generated alarm reasons
 * @param {string} reason - The alarm reason text
 * @returns {string} Translated reason
 */
export function translateAlarmReason(reason) {
    if (!reason) return '';
    
    // Check if the reason matches "No registration found for sensor X"
    if (reason.startsWith('No registration found for sensor')) {
        return t('alarms.noRegistrationFound');
    }
    
    return reason;
}

/**
 * Set language and save to localStorage
 * @param {string} lang - Language code ('en' or 'da')
 */
export function setLanguage(lang) {
    if (translations[lang]) {
        currentLanguage = lang;
        localStorage.setItem('language', lang);
        
        // Update all elements with data-i18n attribute
        updateI18nElements();
        
        // Emit custom event for other modules to react to language change
        window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: lang } }));
    }
}

/**
 * Update all DOM elements with data-i18n attributes
 */
function updateI18nElements() {
    const elements = document.querySelectorAll('[data-i18n]');
    elements.forEach(element => {
        const key = element.getAttribute('data-i18n');
        if (key) {
            element.textContent = t(key);
        }
    });
}

// Initialize translations on page load
if (typeof window !== 'undefined') {
    window.addEventListener('DOMContentLoaded', () => {
        updateI18nElements();
    });
}

/**
 * Get current language
 * @returns {string} Current language code
 */
export function getCurrentLanguage() {
    return currentLanguage;
}

export default {
    translations,
    t,
    translateAlarmReason,
    setLanguage,
    getCurrentLanguage
};
